import { useState, useEffect } from 'react';
import { normalizeAjaxUrl } from '@/utils/normalizeUrl';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Play, 
  Clock, 
  BookOpen, 
  Star, 
  Phone, 
  ChevronDown, 
  HelpCircle, 
  FileText,
  Check,
  Calendar,
  X,
  User,
  ArrowLeft,
  Users,
  Heart
} from 'lucide-react';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

// Custom Backpack Icon
const BackpackIcon = ({ color, textColor, bgLight, char }: { color: string; textColor: string; bgLight: string; char: string | React.ReactNode }) => {
  return (
    <div className={`w-11 h-11 rounded-2xl ${bgLight} flex items-center justify-center flex-shrink-0 relative overflow-hidden transition-all duration-200 group-hover:scale-105`}>
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="relative z-10">
        {/* Top Handle */}
        <path d="M9 5C9 3.34315 10.3431 2 12 2C13.6569 2 15 3.34315 15 5" stroke={color} strokeWidth="2" strokeLinecap="round"/>
        {/* Main Body */}
        <path d="M5 9C5 7.34315 6.34315 6 8 6H16C17.6569 6 19 7.34315 19 9V17C19 19.2091 17.2091 21 15 21H9C6.79086 21 5 19.2091 5 17V9Z" fill="none" stroke={color} strokeWidth="2"/>
        {/* Front Pocket */}
        <path d="M5 14C5 12.8954 5.89543 12 7 12H17C18.1046 12 19 12.8954 19 14V19C19 20.1046 18.1046 21 17 21H7C5.89543 21 5 20.1046 5 19V14Z" fill={color} fillOpacity="0.1" stroke={color} strokeWidth="1.5"/>
        {/* Pocket zipper detail */}
        <path d="M9 14.5H15" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
      {/* Centered text or symbol */}
      <span className="absolute text-[10px] font-english font-bold select-none z-20 pointer-events-none mt-1" style={{ color: textColor }}>
        {char}
      </span>
    </div>
  );
};


const categories = [
  { id: 'এসএসসি', label: 'এসএসসি', subtitle: '৯ম-১০ম শ্রেণী', char: 'S', color: '#F59E0B', bgLight: 'bg-amber-50/70', textColor: '#F59E0B' },
  { id: 'অষ্টম শ্রেণি', label: 'অষ্টম শ্রেণি', subtitle: '৮ম শ্রেণী', char: '8', color: '#F97316', bgLight: 'bg-orange-50/70', textColor: '#F97316' },
  { id: 'সপ্তম শ্রেণি', label: 'সপ্তম শ্রেণি', subtitle: '৭ম শ্রেণী', char: '7', color: '#84CC16', bgLight: 'bg-lime-50/70', textColor: '#84CC16' },
  { id: 'ষষ্ঠ শ্রেণি', label: 'ষষ্ঠ শ্রেণি', subtitle: '৬ষ্ঠ শ্রেণী', char: '6', color: '#A855F7', bgLight: 'bg-purple-50/70', textColor: '#A855F7' },
];

const getSyllabus = (title: string) => {
  const titleStr = title || '';
  if (titleStr.includes('পদার্থবিজ্ঞান')) {
    return [
      { name: 'অধ্যায় ১: ভৌত রাশি ও পরিমাপ', desc: 'ভৌত রাশি, পরিমাপের একক, পরিমাপের যন্ত্রপাতি, ত্রুটি ও নির্ভুলতা।' },
      { name: 'অধ্যায় ২: গতি', desc: 'স্থিতি ও গতি, বিভিন্ন প্রকার গতি, স্কেলার ও ভেক্টর রাশি, দূরত্ব ও সরণ, দ্রুতি ও বেগ, ত্বরণ ও মন্দন।' },
      { name: 'অধ্যায় ৩: বল', desc: 'জড়তা ও বলের গুণগত ধারণা, নিউটনের গতিসূত্র, মৌলিক বল, মহাকর্ষ বল, ঘর্ষণ বল।' },
      { name: 'অধ্যায় ৪: কাজ, ক্ষমতা ও শক্তি', desc: 'কাজ, বিভব শক্তি, গতি শক্তি, শক্তির রূপান্তর, কর্মদক্ষতা ও ক্ষমতা।' },
    ];
  } else if (titleStr.includes('গণিত')) {
    return [
      { name: 'অধ্যায় ১: বাস্তব সংখ্যা', desc: 'বাস্তব সংখ্যার শ্রেণীবিভাগ, সদৃশ ও অসদৃশ আবৃত্ত দশমিক, ভগ্নাংশ ও মূলদ-অমূলদ নির্ণয়।' },
      { name: 'অধ্যায় ২: সেট ও ফাংশন', desc: 'সেট প্রকাশের পদ্ধতি, সসীম ও অসীম সেট, শক্তি সেট, কার্তেজীয় গুণজ, ডোমেন ও রেঞ্জ।' },
      { name: 'অধ্যায় ৩: বীজগণিতীয় রাশি', desc: 'বর্গ ও ঘন সংবলিত সূত্রাবলী, মান নির্ণয়, উৎপাদকে বিশ্লেষণ, ভাগশেষ উপপাদ্য।' },
      { name: 'অধ্যায় ৪: সূচক ও লগারিদম', desc: 'সূচক ও লগারিদমের সূত্রাবলী, বৈজ্ঞানিক রূপ, সাধারণ ও প্রাকৃতিক লগারিদম।' },
    ];
  } else if (titleStr.includes('রসায়ন')) {
    return [
      { name: 'অধ্যায় ১: রসায়নের ধারণা', desc: 'রসায়নের পরিচিতি, পরিধি, অনুসন্ধান গবেষণা, রসায়নে ব্যবহৃত বিভিন্ন সাংকেতিক চিহ্নের অর্থ।' },
      { name: 'অধ্যায় ২: পদার্থের অবস্থা', desc: 'কণার গতিতত্ত্ব, ব্যাপন ও নিঃসরণ, গলনাঙ্ক ও স্ফুটনাঙ্ক, শীতলীকরণ বক্ররেখা।' },
      { name: 'অধ্যায় ৩: পদার্থের গঠন', desc: 'মৌলিক ও যৌগিক পদার্থ, পরমাণু ও অণু, প্রতীক ও সংকেত, পরমাণুর ভেতরের কণা, পারমাণবিক সংখ্যা।' },
    ];
  } else {
    return [
      { name: 'ইউনিট ১: বেসিক ফাউন্ডেশন ক্লাস', desc: 'বিষয়ভিত্তিক মূল আলোচনা, গুরুত্বপূর্ণ সংজ্ঞা ও ধারণার প্রাথমিক ক্লাস।' },
      { name: 'ইউনিট ২: বিস্তারিত তাত্ত্বিক আলোচনা', desc: 'পূর্ণাঙ্গ সিলেবাস ভিত্তিক অধ্যায়ের চুলচেরা বিশ্লেষণ ও টপিকওয়াইজ লার্নিং।' },
      { name: 'ইউনিট ৩: সৃজনশীল সমাধান ক্লাস', desc: 'বোর্ড পরীক্ষা ও স্কুলের প্রশ্ন বিশ্লেষণ করে সৃজনশীল প্রশ্নের উত্তর সাজানোর ক্লাস।' },
    ];
  }
};

interface CoursesViewProps {
  selectedCourseId: number | null;
  setSelectedCourseId: (id: number | null) => void;
}

export default function CoursesView({ selectedCourseId, setSelectedCourseId }: CoursesViewProps) {
  const [activeCategory, setActiveCategory] = useState<string>('এসএসসি');
  const [sscGroup, setSscGroup] = useState<'all' | 'science' | 'commerce' | 'humanities'>('all');
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [openChapter, setOpenChapter] = useState<number | null>(null);
  
  // Checkout Modal and Enrollment state
  const [showEnrollModal, setShowEnrollModal] = useState<boolean>(false);
  const [selectedPackage, setSelectedPackage] = useState<'trial' | 'full'>('trial');
  const [enrolledCourseIds, setEnrolledCourseIds] = useState<Set<number>>(new Set());
  const [showSuccessToast, setShowSuccessToast] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>('');

  // Manual payment states
  const [enrollStep, setEnrollStep] = useState<'select' | 'checkout'>('select');
  const [manualMethods, setManualMethods] = useState<any[]>([]);
  const [activePaymentMethod, setActivePaymentMethod] = useState<string>('bkash');
  const [senderNumber, setSenderNumber] = useState<string>('');
  const [transactionId, setTransactionId] = useState<string>('');
  const [isSubmittingPayment, setIsSubmittingPayment] = useState<boolean>(false);

  const [coursesList, setCoursesList] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedInstructor, setSelectedInstructor] = useState<string | null>(null);
  const [publicInstructors, setPublicInstructors] = useState<any[]>([]);
  const [publicSettings, setPublicSettings] = useState<any>({});
  const [isPlayingVideo, setIsPlayingVideo] = useState<boolean>(false);
  const [isStudyMode, setIsStudyMode] = useState<boolean>(false);
  const [activeDemoVideoUrl, setActiveDemoVideoUrl] = useState<string | null>(null);
  const [activeLesson, setActiveLesson] = useState<any>(null);
  const [activeTopic, setActiveTopic] = useState<any>(null);
  const [completedLessons, setCompletedLessons] = useState<Set<number | string>>(new Set());
  const [expandedTopics, setExpandedTopics] = useState<Set<number | string>>(new Set());

  const isFeatureActive = (key: string, defaultValue = true): boolean => {
    const val = publicSettings[key] !== undefined ? publicSettings[key] : publicSettings[key.toLowerCase()];
    if (val === undefined) return defaultValue;
    return val === "true" || val === true || val === 1 || val === "1";
  };

  // Fetch public instructors and settings list on mount
  useEffect(() => {
    const fetchPublicData = async () => {
      const shAuth = (window as any).shAuth || {};
      const ajaxUrl = shAuth?.ajax_url || '/wp-admin/admin-ajax.php';

      try {
        const response = await fetch(ajaxUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams({
            action: 'hub_get_public_instructors'
          }).toString()
        });
        const res = await response.json();
        if (res.success && Array.isArray(res.data)) {
          setPublicInstructors(res.data);
        }
      } catch (err) {
        console.error('Error fetching public instructors:', err);
      }

      try {
        const response = await fetch(ajaxUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams({
            action: 'hub_get_public_settings'
          }).toString()
        });
        const res = await response.json();
        if (res.success && res.data) {
          setPublicSettings(res.data);
        }
      } catch (err) {
        console.error('Error fetching public settings:', err);
      }
    };
    fetchPublicData();
  }, []);
  const getInstructorsForCourse = (_courseTitle: string, mainInstructorName?: string) => {
    const defaultList = [
      { name: "Sharlin Ulfatt Bhuiyan", role: "English Mentor", exp: "১.৫+ বছরের অভিজ্ঞতা", score: "Band Score: 8.0", avatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&h=150&fit=crop" },
      { name: "Saraf Samantha Khan", role: "Chemistry Expert", exp: "১.৫+ বছরের অভিজ্ঞতা", score: "Band Score: 8.0", avatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150&h=150&fit=crop" },
      { name: "Zawad Hasan Adib", role: "Physics Instructor", exp: "৬+ বছরের অভিজ্ঞতা", score: "Band Score: 8.0", avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop" },
      { name: "Rifah Salsabil", role: "Biology Trainer", exp: "৫+ বছরের অভিজ্ঞতা", score: "Band Score: 7.5", avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&h=150&fit=crop" }
    ];

    const syncedList = defaultList.map(inst => {
      const dbInstructor = publicInstructors.find(i => i.name.toLowerCase() === inst.name.toLowerCase());
      if (dbInstructor) {
        return {
          name: dbInstructor.name,
          role: dbInstructor.designation || inst.role,
          exp: dbInstructor.experience || inst.exp,
          score: dbInstructor.rating ? (dbInstructor.rating.includes('Band') ? dbInstructor.rating : `রেটিং: ${dbInstructor.rating}`) : inst.score,
          avatar: dbInstructor.avatar || inst.avatar
        };
      }
      return inst;
    });

    if (mainInstructorName && mainInstructorName !== 'শিক্ষক') {
      const dbInstructor = publicInstructors.find(i => i.name.toLowerCase() === mainInstructorName.toLowerCase());
      if (dbInstructor) {
        const exists = syncedList.find(i => i.name.toLowerCase() === mainInstructorName.toLowerCase());
        if (exists) {
          return [
            exists,
            ...syncedList.filter(i => i.name.toLowerCase() !== mainInstructorName.toLowerCase())
          ];
        } else {
          return [
            { 
              name: dbInstructor.name, 
              role: dbInstructor.designation || "মাস্টার ট্রেইনার, শুদ্ধশিক্ষা", 
              exp: dbInstructor.experience || "৫+ বছরের অভিজ্ঞতা", 
              score: dbInstructor.rating ? `রেটিং: ${dbInstructor.rating}` : "শুদ্ধশিক্ষা মেন্টর",
              avatar: dbInstructor.avatar
            },
            ...syncedList.slice(0, 3)
          ];
        }
      } else {
        const exists = syncedList.find(i => i.name.toLowerCase() === mainInstructorName.toLowerCase());
        if (exists) {
          return [
            exists,
            ...syncedList.filter(i => i.name.toLowerCase() !== mainInstructorName.toLowerCase())
          ];
        } else {
          return [
            { name: mainInstructorName, role: "মাস্টার ট্রেইনার, শুদ্ধশিক্ষা", exp: "৫+ বছরের অভিজ্ঞতা", score: "শুদ্ধশিক্ষা মেন্টর", avatar: "" },
            ...syncedList.slice(0, 3)
          ];
        }
      }
    }
    return syncedList;
  };

  useEffect(() => {
    setIsPlayingVideo(false);
    if (!selectedCourseId) {
      setIsStudyMode(false);
      setActiveLesson(null);
      setActiveTopic(null);
    }
  }, [selectedCourseId]);

  // Fetch manual payment methods when enrollment modal is opened
  useEffect(() => {
    if (showEnrollModal) {
      setEnrollStep('select');
      setSenderNumber('');
      setTransactionId('');
      
      const fetchMethods = async () => {
        try {
          const shAuth = (window as any).shAuth || {};
          const ajaxUrl = shAuth?.ajax_url || '/wp-admin/admin-ajax.php';
          const response = await fetch(ajaxUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams({
              action: 'sh_get_manual_payment_methods'
            }).toString()
          });
          const res = await response.json();
          if (res.success && Array.isArray(res.data)) {
            setManualMethods(res.data);
            if (res.data.length > 0) {
              setActivePaymentMethod(res.data[0].id);
            }
          }
        } catch (err) {
          console.error('Error fetching manual methods:', err);
        }
      };
      fetchMethods();
    }
  }, [showEnrollModal]);

  useEffect(() => {
    if (selectedCourseId && isStudyMode && !activeLesson) {
      const selectedCourse = coursesList.find(c => c.id === selectedCourseId);
      if (selectedCourse) {
        const curr = selectedCourse.curriculum;
        if (Array.isArray(curr) && curr.length > 0) {
          const firstTopic = curr[0];
          if (firstTopic && Array.isArray(firstTopic.lessons) && firstTopic.lessons.length > 0) {
            setActiveTopic(firstTopic);
            setActiveLesson(firstTopic.lessons[0]);
            setExpandedTopics(new Set([firstTopic.id || firstTopic.title]));
          }
        }
      }
    }
  }, [selectedCourseId, isStudyMode, coursesList, activeLesson]);

  const toBengaliNumber = (num: number | string) => {
    const en = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
    const bn = ['০', '১', '২', '৩', '৪', '৫', '৬', '৭', '৮', '৯'];
    let str = String(num);
    for (let i = 0; i < 10; i++) {
      str = str.replace(new RegExp(en[i], 'g'), bn[i]);
    }
    return str;
  };

  const getEmbedUrl = (url: string) => {
    if (!url) return null;
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
      const match = url.match(regExp);
      if (match && match[2].length === 11) {
        return `https://www.youtube.com/embed/${match[2]}?autoplay=1`;
      }
    }
    if (url.includes('vimeo.com')) {
      const match = url.match(/vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/([^\/]*)\/posts\/|album\/(\d+)\/video\/|)(\d+)(?:$|\/|\?)/);
      if (match && match[3]) {
        return `https://player.vimeo.com/video/${match[3]}?autoplay=1`;
      }
    }
    return null;
  };

  // Custom Helper to map category colors
  const getCategoryColor = (category: string) => {
    const normalized = category ? category.trim() : '';
    if (normalized.includes('এসএসসি') || normalized.includes('SSC')) return '#00b09b';
    if (normalized.includes('অষ্টম') || normalized.includes('class-8') || normalized.includes('class 8')) return '#f97316';
    if (normalized.includes('সপ্তম') || normalized.includes('class-7') || normalized.includes('class 7')) return '#8b5cf6';
    if (normalized.includes('ষষ্ঠ') || normalized.includes('class-6') || normalized.includes('class 6')) return '#3b82f6';
    return '#00b09b';
  };

  // Reusable Course Card component
  const CourseCard = ({ course }: { course: any }) => {
    const hasThumb = course.thumbnail && !course.thumbnail.includes("544716278-ca5e3f4abd8c");
    const catColor = getCategoryColor(course.category);

    return (
      <div className="bg-white rounded-[24px] border border-slate-100 overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 group flex flex-col h-full text-left">
        {/* Thumbnail Area */}
        <div 
          className="aspect-video w-full relative overflow-hidden flex-shrink-0 bg-slate-50 flex items-center justify-center"
          style={hasThumb ? {} : { background: `linear-gradient(135deg, ${catColor === '#00b09b' ? '#e53935' : catColor === '#f97316' ? '#f97316' : catColor === '#8b5cf6' ? '#8b5cf6' : '#3b82f6'}, ${catColor === '#00b09b' ? '#c62828' : catColor === '#f97316' ? '#ea580c' : catColor === '#8b5cf6' ? '#7c3aed' : '#1d4ed8'})` }}
        >
          {hasThumb ? (
            <img 
              src={course.thumbnail} 
              alt={course.title} 
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 z-0"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center p-4 text-center z-10">
              <span className="font-bangla text-sm font-bold text-white leading-snug drop-shadow-md">
                {course.title}
              </span>
            </div>
          )}
          
          {/* Category Badge */}
          {course.category && (
            <span 
              className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-white text-[10px] font-bold shadow-sm z-20"
              style={{ backgroundColor: catColor }}
            >
              {course.category}
            </span>
          )}

          {/* Bundle Badge */}
          {course.isBundle && (
            <span className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-red-500 text-white text-[10px] font-bold shadow-sm z-20">
              বান্ডেল
            </span>
          )}
        </div>

        {/* Card Body */}
        <div className="p-5 flex-1 flex flex-col justify-between gap-3.5">
          <div className="space-y-2">
            {/* Title */}
            <h3 
              onClick={() => setSelectedCourseId(course.id)}
              className="font-bangla text-base font-bold text-[#1F1F1F] line-clamp-2 leading-snug hover:text-[#00CBA9] transition-colors cursor-pointer"
            >
              {course.title}
            </h3>

            {/* Meta row: Instructor + Lessons */}
            <div className="flex items-center gap-4 text-[11px] text-gray-500 font-bangla font-medium">
              <span 
                onClick={(e) => {
                  e.stopPropagation();
                  if (course.instructor) setSelectedInstructor(course.instructor);
                }}
                className="flex items-center gap-1.5 cursor-pointer hover:text-[#00C9A7] transition-colors"
              >
                <User className="w-3.5 h-3.5 text-gray-400" strokeWidth={2.5} />
                {course.instructor || 'শিক্ষক'}
              </span>
              {course.totalLessons > 0 && (
                <span className="flex items-center gap-1.5">
                  <BookOpen className="w-3.5 h-3.5 text-gray-400" strokeWidth={2.5} />
                  {course.totalLessons}টি ক্লাস
                </span>
              )}
            </div>
          </div>

          {/* Footer: Price + Button */}
          <div className="flex items-center justify-between border-t border-slate-50 pt-3.5 mt-2">
            <div className="flex flex-col">
              <span className="font-bangla text-lg font-extrabold text-[#00CBA9]">৳{course.price}</span>
              {course.regularPrice > course.price && (
                <span className="font-bangla text-[10px] text-gray-400 line-through">৳{course.regularPrice}</span>
              )}
            </div>
            <button 
              onClick={() => setSelectedCourseId(course.id)}
              className="font-bangla text-[11px] font-bold bg-[#00CBA9] text-white px-4 py-2.5 rounded-xl hover:bg-[#00b898] transition-colors cursor-pointer shadow-sm border-0"
            >
              {enrolledCourseIds.has(course.id) ? 'পড়াশোনা শুরু' : 'বিস্তারিত দেখুন'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Reusable Skeleton Loader for courses grid
  const SkeletonLoader = () => {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 w-full text-left">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="bg-white rounded-[24px] border border-slate-100 overflow-hidden shadow-sm flex flex-col h-full animate-pulse">
            {/* Aspect Video Thumbnail skeleton */}
            <div className="aspect-video w-full bg-slate-100" />
            
            {/* Body */}
            <div className="p-5 flex-1 flex flex-col justify-between gap-4">
              <div className="space-y-3">
                {/* Title skeleton */}
                <div className="h-5 bg-slate-100 rounded-lg w-5/6" />
                <div className="h-5 bg-slate-100 rounded-lg w-2/3" />
                
                {/* Meta row skeleton */}
                <div className="flex gap-4 pt-2">
                  <div className="h-4 bg-slate-100 rounded-md w-1/4" />
                  <div className="h-4 bg-slate-100 rounded-md w-1/4" />
                </div>
              </div>

              {/* Footer row skeleton */}
              <div className="flex items-center justify-between border-t border-slate-50 pt-4 mt-3">
                <div className="h-6 bg-slate-100 rounded-lg w-1/4" />
                <div className="h-9 bg-slate-100 rounded-xl w-1/3" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        setIsLoading(true);
        const rawAjaxUrl = (window as any).sh_react_object?.ajax_url || 'https://shuddhoshiksha.com/wp-admin/admin-ajax.php';
        const ajaxUrl = normalizeAjaxUrl(rawAjaxUrl);
        const params = new URLSearchParams();
        params.append('action', 'sh_get_react_courses');
        
        const response = await fetch(ajaxUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: params.toString(),
        });
        
        const data = await response.json();
        if (data.success) {
          setCoursesList(data.data || []);
        } else {
          setError(data.data || 'Failed to load courses');
        }
      } catch (err: any) {
        setError(err.message || 'Error fetching courses');
      } finally {
        setIsLoading(false);
      }
    };
    fetchCourses();
  }, []);

  const mappedCourses = (coursesList || []).map(c => {
    if (!c) return null;

    const titleStr = c.title || '';
    const titleLower = titleStr.toLowerCase();
    
    let tagsStr = '';
    if (c.tags) {
      if (Array.isArray(c.tags)) {
        tagsStr = c.tags.join(' ');
      } else {
        tagsStr = String(c.tags);
      }
    }
    const tagLower = tagsStr.toLowerCase();

    // Determine category (class level) from tags
    let category = 'এসএসসি';
    if (tagLower) {
      if (tagLower.includes('ssc') || tagLower.includes('এসএসসি')) {
        category = 'এসএসসি';
      } else if (tagLower.includes('অষ্টম') || tagLower.includes('class-8') || tagLower.includes('class 8')) {
        category = 'অষ্টম শ্রেণি';
      } else if (tagLower.includes('সপ্তম') || tagLower.includes('class-7') || tagLower.includes('class 7')) {
        category = 'সপ্তম শ্রেণি';
      } else if (tagLower.includes('ষষ্ঠ') || tagLower.includes('class-6') || tagLower.includes('class 6')) {
        category = 'ষষ্ঠ শ্রেণি';
      }
    } else if (titleLower) {
      if (titleLower.includes('ssc') || titleLower.includes('এসএসসি')) {
        category = 'এসএসসি';
      } else if (titleLower.includes('অষ্টম') || titleLower.includes('class 8') || titleLower.includes('class-8')) {
        category = 'অষ্টম শ্রেণি';
      } else if (titleLower.includes('সপ্তম') || titleLower.includes('class 7') || titleLower.includes('class-7')) {
        category = 'সপ্তম শ্রেণি';
      } else if (titleLower.includes('ষষ্ঠ') || titleLower.includes('class 6') || titleLower.includes('class-6')) {
        category = 'ষষ্ঠ শ্রেণি';
      }
    }

    // Determine subGroup (group/department) from categories
    let catsStr = '';
    if (c.categories) {
      if (Array.isArray(c.categories)) {
        catsStr = c.categories.join(' ');
      } else {
        catsStr = String(c.categories);
      }
    }
    const catLower = catsStr.toLowerCase();

    let group = 'all'; // default
    if (catLower) {
      if (catLower.includes('বিজ্ঞান') || catLower.includes('science')) {
        group = 'science';
      } else if (catLower.includes('ব্যবসায়') || catLower.includes('business') || catLower.includes('commerce') || catLower.includes('শিক্ষা')) {
        group = 'commerce';
      } else if (catLower.includes('মানবিক') || catLower.includes('humanities')) {
        group = 'humanities';
      }
    } else if (titleLower) {
      if (titleLower.includes('বিজ্ঞান') || titleLower.includes('science')) {
        group = 'science';
      } else if (titleLower.includes('ব্যবসায়') || titleLower.includes('business') || titleLower.includes('commerce') || titleLower.includes('শিক্ষা')) {
        group = 'commerce';
      } else if (titleLower.includes('মানবিক') || titleLower.includes('humanities')) {
        group = 'humanities';
      }
    }

    // Determine subtitle
    let subtitle = '';
    if (category === 'এসএসসি') {
      subtitle = 'SSC 2027';
    } else if (category === 'অষ্টম শ্রেণি') {
      subtitle = 'Class 8';
    } else if (category === 'সপ্তম শ্রেণি') {
      subtitle = 'Class 7';
    } else if (category === 'ষষ্ঠ শ্রেণি') {
      subtitle = 'Class 6';
    }

    // Determine color theme based on ID
    const colors = [
      'from-indigo-500 to-blue-600',
      'from-emerald-500 to-teal-600',
      'from-violet-500 to-purple-600',
      'from-amber-500 to-orange-600',
      'from-rose-500 to-pink-600',
      'from-cyan-500 to-sky-600'
    ];
    const colorId = typeof c.id === 'number' ? c.id : 0;
    const color = colors[colorId % colors.length];

    return {
      ...c,
      category,
      group,
      subtitle,
      color,
      totalLessons: c.lessons || 0,
      completedLessons: 0,
      progress: 0,
    };
  }).filter(Boolean);



  const selectedCourse = mappedCourses.find(c => c.id === selectedCourseId);
  const filteredClassCourses = mappedCourses.filter(course => course.category === activeCategory);

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const toggleChapter = (index: number) => {
    setOpenChapter(openChapter === index ? null : index);
  };

  const triggerToast = (msg: string) => {
    setToastMessage(msg);
    setShowSuccessToast(true);
    setTimeout(() => {
      setShowSuccessToast(false);
    }, 4000);
  };

  const handleEnrollProceed = async () => {
    if (selectedCourseId) {
      if (selectedPackage === 'trial') {
        try {
          const shAuth = (window as any).shAuth || {};
          const ajaxUrl = shAuth?.ajax_url || '/wp-admin/admin-ajax.php';
          const nonce = shAuth?.nonce || '';
          
          const response = await fetch(ajaxUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams({
              action: 'sh_enroll_free_trial',
              nonce: nonce,
              course_id: selectedCourseId.toString()
            }).toString()
          });
          const res = await response.json();
          if (res.success) {
            const updated = new Set(enrolledCourseIds);
            updated.add(selectedCourseId);
            setEnrolledCourseIds(updated);

            // Update window global user data for real-time reactivity without page load
            if (shAuth && shAuth.user) {
              if (!Array.isArray(shAuth.user.enrolled_courses)) {
                shAuth.user.enrolled_courses = [];
              }
              if (!shAuth.user.enrolled_courses.includes(selectedCourseId)) {
                shAuth.user.enrolled_courses.push(selectedCourseId);
              }
            }

            setShowEnrollModal(false);
            triggerToast('অভিনন্দন! ৫ দিনের ফ্রি ট্রায়াল অ্যাক্টিভেট করা হয়েছে।');
          } else {
            alert(res.data || 'ফ্রি ট্রায়াল সক্রিয় করতে ব্যর্থ হয়েছে।');
          }
        } catch (err) {
          console.error('Error activating free trial:', err);
          alert('সার্ভারে যোগাযোগ করতে ব্যর্থ হয়েছে।');
        }
      } else {
        // Full package goes to manual payment checkout step
        if (manualMethods.length > 0) {
          setEnrollStep('checkout');
        } else {
          // Fallback if no manual methods configured
          const updated = new Set(enrolledCourseIds);
          updated.add(selectedCourseId);
          setEnrolledCourseIds(updated);
          setShowEnrollModal(false);
          triggerToast('অভিনন্দন! কোর্সটি সফলভাবে ক্রয় করা হয়েছে।');
        }
      }
    }
  };

  const handleConfirmManualPayment = async () => {
    if (!selectedCourseId) return;
    if (!senderNumber.trim() || !transactionId.trim()) {
      alert('অনুগ্রহ করে আপনার সেন্ডার মোবাইল নম্বর এবং ট্রানজেকশন আইডি প্রদান করুন।');
      return;
    }

    setIsSubmittingPayment(true);
    try {
      const shAuth = (window as any).shAuth || {};
      const ajaxUrl = shAuth?.ajax_url || '/wp-admin/admin-ajax.php';
      const nonce = shAuth?.nonce || '';
      const selectedMethodName = manualMethods.find(m => m.id === activePaymentMethod)?.name || 'Manual Payment';

      const response = await fetch(ajaxUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          action: 'sh_submit_manual_course_order',
          nonce: nonce,
          course_id: selectedCourseId.toString(),
          payment_method: selectedMethodName,
          sender_number: senderNumber.trim(),
          transaction_id: transactionId.trim()
        }).toString()
      });

      const res = await response.json();
      if (res.success) {
        alert(res.data?.message || 'অর্ডারটি সফলভাবে সাবমিট করা হয়েছে! অ্যাডমিন পেমেন্ট ভেরিফাই করে আপনার কোর্সটি সক্রিয় করে দেবেন।');
        setShowEnrollModal(false);
        triggerToast('পেমেন্ট ভেরিফিকেশন পেন্ডিং রয়েছে।');
      } else {
        alert(res.data || 'অর্ডার সাবমিট করতে ব্যর্থ হয়েছে।');
      }
    } catch (err) {
      alert('সার্ভারে যোগাযোগ করতে ব্যর্থ হয়েছে।');
    } finally {
      setIsSubmittingPayment(false);
    }
  };

  const CategoryButton = ({ cat, active, onClick }: { cat: typeof categories[0], active: boolean, onClick: () => void }) => {
    return (
      <button
        onClick={onClick}
        className={`w-full flex items-center gap-3.5 p-3.5 rounded-2xl transition-all duration-200 text-left cursor-pointer group border ${
          active 
            ? 'bg-white border-transparent ring-2 ring-[#00CBA9] shadow-sm' 
            : 'bg-white border-gray-100 hover:border-gray-200'
        }`}
      >
        <BackpackIcon color={cat.color} textColor={cat.textColor} bgLight={cat.bgLight} char={cat.char} />
        <div className="min-w-0">
          <span className={`font-bangla text-[15px] block truncate ${
            active ? 'text-[#1F1F1F] font-bold' : 'text-gray-700 group-hover:text-[#1F1F1F] font-semibold'
          }`}>
            {cat.label}
          </span>
          <span className="font-bangla text-[11px] text-gray-400 block mt-0.5">{cat.subtitle}</span>
        </div>
      </button>
    );
  };

  const renderStudyView = () => {
    if (!selectedCourse) return null;
    
    const curriculum = selectedCourse.curriculum || [];
    
    // Count total lessons
    const totalLessonsCount = curriculum.reduce((acc: number, t: any) => acc + (t.lessons?.length || 0), 0);
    const completedCount = completedLessons.size;
    const progressPercent = totalLessonsCount > 0 ? Math.round((completedCount / totalLessonsCount) * 100) : 0;
    
    const progressPercentBn = toBengaliNumber(progressPercent);
    const totalLessonsBn = toBengaliNumber(totalLessonsCount);
    const completedCountBn = toBengaliNumber(completedCount);

    const activeLessonVideoUrl = activeLesson?.videoUrl || '';
    const activeLessonEmbedUrl = getEmbedUrl(activeLessonVideoUrl);

    // Find next lesson to allow easy navigation
    let nextLesson: any = null;
    let nextTopic: any = null;
    let foundActive = false;
    for (let tIdx = 0; tIdx < curriculum.length; tIdx++) {
      const topic = curriculum[tIdx];
      const lessons = topic.lessons || [];
      for (let lIdx = 0; lIdx < lessons.length; lIdx++) {
        const lesson = lessons[lIdx];
        if (foundActive) {
          nextLesson = lesson;
          nextTopic = topic;
          break;
        }
        if (lesson.id === activeLesson?.id || lesson.title === activeLesson?.title) {
          foundActive = true;
        }
      }
      if (nextLesson) break;
    }

    const handleMarkComplete = () => {
      if (!activeLesson) return;
      const lessonKey = activeLesson.id || activeLesson.title;
      const newCompleted = new Set(completedLessons);
      if (newCompleted.has(lessonKey)) {
        newCompleted.delete(lessonKey);
      } else {
        newCompleted.add(lessonKey);
      }
      setCompletedLessons(newCompleted);
    };

    const handleNextLesson = () => {
      if (nextLesson) {
        setActiveLesson(nextLesson);
        if (nextTopic) {
          setActiveTopic(nextTopic);
          setExpandedTopics(prev => {
            const updated = new Set(prev);
            updated.add(nextTopic.id || nextTopic.title);
            return updated;
          });
        }
      }
    };

    const toggleTopicAccordion = (topicKey: string | number) => {
      setExpandedTopics(prev => {
        const updated = new Set(prev);
        if (updated.has(topicKey)) {
          updated.delete(topicKey);
        } else {
          updated.add(topicKey);
        }
        return updated;
      });
    };

    return (
      <div className="space-y-6 bg-[#F4F2FF] dark:bg-[#0f172a] -mx-4 md:-mx-8 px-4 md:px-8 py-6 rounded-3xl min-h-screen text-slate-800 dark:text-slate-100 pb-32">
        {/* Top Navigation Row */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-200/60 pb-4">
          <button 
            onClick={() => setIsStudyMode(false)}
            className="flex items-center gap-2 text-sm text-[#00CBA9] font-bangla font-semibold hover:underline cursor-pointer bg-transparent border-none self-start"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-4 h-4"><path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18" /></svg>
            কোর্স বিস্তারিত পাতায় ফিরে যান
          </button>
          
          {/* Breadcrumbs */}
          <div className="font-bangla text-xs text-slate-500 flex items-center gap-1.5 flex-wrap">
            <span className="cursor-pointer hover:text-[#00CBA9]" onClick={() => { setIsStudyMode(false); setSelectedCourseId(null); }}>কোর্সসমূহ</span>
            <span>&gt;</span>
            <span className="cursor-pointer hover:text-[#00CBA9]" onClick={() => setIsStudyMode(false)}>{selectedCourse.title}</span>
            {activeTopic && (
              <>
                <span>&gt;</span>
                <span className="text-slate-700 font-semibold">{activeTopic.title}</span>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Left Column (Lesson Content Area) */}
          <div className="lg:col-span-8 space-y-6">
            
            {/* Video Player or Material Placeholder */}
            <div className="w-full aspect-video bg-slate-950 rounded-3xl overflow-hidden shadow-xl border border-slate-900/10 relative">
              {activeLessonVideoUrl ? (
                activeLessonEmbedUrl ? (
                  <iframe 
                    src={activeLessonEmbedUrl} 
                    className="absolute inset-0 w-full h-full border-0" 
                    allow="autoplay; fullscreen" 
                    allowFullScreen 
                  />
                ) : (
                  <video 
                    src={activeLessonVideoUrl} 
                    className="absolute inset-0 w-full h-full object-cover" 
                    controls 
                    autoPlay 
                  />
                )
              ) : (
                /* Document/Text Lesson Placeholder */
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-900 text-center p-6 space-y-4">
                  <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center text-white">
                    <FileText className="w-8 h-8" />
                  </div>
                  <div className="space-y-1">
                    <h3 className="font-bangla text-lg font-bold text-white">এটি একটি পাঠ্য ও নোট লেসন</h3>
                    <p className="font-bangla text-xs text-slate-400">ভিডিও ব্যতীত এই লেসনে পড়ার গুরুত্বপূর্ণ টপিক ও নোট নিচে যুক্ত করা হয়েছে।</p>
                  </div>
                </div>
              )}
            </div>

            {/* Lesson Info Card */}
            <div className="bg-white rounded-3xl p-6 md:p-8 shadow-sm border border-slate-100/50 space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 pb-5">
                <div>
                  <h1 className="font-bangla text-xl md:text-2xl font-extrabold text-[#1F1F1F]">
                    {activeLesson?.title || 'লেসন লোড হচ্ছে...'}
                  </h1>
                  <p className="font-bangla text-xs text-slate-400 mt-1">
                    টপিক: {activeTopic?.title || 'লোডিং...'}
                  </p>
                </div>

                {/* Mark Complete & Navigation Actions */}
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleMarkComplete}
                    className={`px-4 py-2.5 rounded-xl font-bangla text-xs font-bold transition-all flex items-center gap-1.5 border-0 cursor-pointer ${
                      completedLessons.has(activeLesson?.id || activeLesson?.title)
                        ? 'bg-[#00C9A7]/10 text-[#00C9A7]'
                        : 'bg-[#00CBA9] text-white hover:bg-[#00b898]'
                    }`}
                  >
                    <Check className="w-4 h-4" />
                    {completedLessons.has(activeLesson?.id || activeLesson?.title) ? 'সম্পন্ন হয়েছে' : 'সম্পন্ন চিহ্নিত করুন'}
                  </button>

                  {nextLesson && (
                    <button
                      onClick={handleNextLesson}
                      className="px-4 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-bangla text-xs font-bold transition-all flex items-center gap-1 border-0 cursor-pointer"
                    >
                      পরবর্তী লেসন →
                    </button>
                  )}
                </div>
              </div>

              {/* Lesson Description/Content */}
              <div className="space-y-4">
                <h3 className="font-bangla text-base font-extrabold text-[#1F1F1F]">লেকচার আলোচনা ও বিবরণ:</h3>
                {activeLesson?.content ? (
                  <div 
                    className="font-bangla text-sm text-slate-600 leading-relaxed font-medium whitespace-pre-wrap bg-slate-50/50 p-5 rounded-2xl border border-slate-100"
                    dangerouslySetInnerHTML={{ __html: activeLesson.content }}
                  />
                ) : (
                  <div className="font-bangla text-sm text-slate-500 italic bg-slate-50/50 p-5 rounded-2xl border border-slate-100">
                    এই লেসনের জন্য কোনো টেক্সট আলোচনা বিবরণ যুক্ত করা হয়নি। লেসনটির মূল আলোচনা ভিডিওতে রয়েছে।
                  </div>
                )}
              </div>

              {/* Chapter Materials (চ্যাপ্টার ম্যাটেরিয়াল) */}
              <div className="space-y-4 pt-2">
                <h3 className="font-bangla text-base font-extrabold text-[#1F1F1F]">চ্যাপ্টার ম্যাটেরিয়াল ও হ্যান্ডনোট</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Active Lesson Notes/Attachments */}
                  {activeLesson?.attachments && activeLesson.attachments.length > 0 ? (
                    activeLesson.attachments.map((attach: any, idx: number) => (
                      <a 
                        key={idx}
                        href={attach.url || '#'}
                        target="_blank"
                        rel="noreferrer"
                        className="bg-white hover:bg-slate-50 border border-slate-100 rounded-2xl p-4 flex items-center justify-between gap-4 transition-all no-underline group shadow-sm hover:shadow"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-orange-50 text-orange-500 flex items-center justify-center">
                            <FileText className="w-5 h-5" />
                          </div>
                          <div>
                            <div className="font-bangla text-xs font-bold text-slate-800">{attach.name || 'লেকচার শিট পিডিএফ'}</div>
                            <div className="font-bangla text-[10px] text-slate-400">ডাউনলোড করে পড়ুন</div>
                          </div>
                        </div>
                        <div className="w-8 h-8 rounded-lg bg-[#00C9A7]/10 text-[#00C9A7] flex items-center justify-center group-hover:scale-105 transition-transform">
                          ↓
                        </div>
                      </a>
                    ))
                  ) : (
                    /* Default Fallback notes just like mockup screen */
                    <>
                      <div className="bg-white border border-slate-100 rounded-2xl p-4 flex items-center justify-between gap-4 shadow-sm">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-orange-50 text-orange-500 flex items-center justify-center flex-shrink-0">
                            <FileText className="w-5 h-5" />
                          </div>
                          <div>
                            <div className="font-bangla text-xs font-bold text-slate-800">লেকচার শিট ও ই-বুক নোট</div>
                            <div className="font-bangla text-[10px] text-slate-400">এই অধ্যায়ের মূল হ্যান্ডনোট ও সূত্রাবলী সম্বলিত শিট</div>
                          </div>
                        </div>
                        <button 
                          onClick={() => triggerToast("ফাইল ডাউনলোড শুরু হচ্ছে...")}
                          className="w-8 h-8 rounded-lg bg-[#00C9A7]/10 text-[#00C9A7] flex items-center justify-center border-0 cursor-pointer"
                        >
                          ↓
                        </button>
                      </div>
                      <div className="bg-white border border-slate-100 rounded-2xl p-4 flex items-center justify-between gap-4 shadow-sm">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-[#00A3FF]/10 text-[#00A3FF] flex items-center justify-center flex-shrink-0">
                            <FileText className="w-5 h-5" />
                          </div>
                          <div>
                            <div className="font-bangla text-xs font-bold text-slate-800">অধ্যায়ভিত্তিক বোর্ড প্রশ্ন সমাধান গাইড</div>
                            <div className="font-bangla text-[10px] text-slate-400">বিগত ৫ বছরের বোর্ড পরীক্ষার পূর্ণাঙ্গ সমাধান নোট</div>
                          </div>
                        </div>
                        <button 
                          onClick={() => triggerToast("ফাইল ডাউনলোড শুরু হচ্ছে...")}
                          className="w-8 h-8 rounded-lg bg-[#00C9A7]/10 text-[#00C9A7] flex items-center justify-center border-0 cursor-pointer"
                        >
                          ↓
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column (Course Syllabus Sidebar) */}
          <div className="lg:col-span-4 space-y-6">
            
            {/* Syllabus card */}
            <div className="bg-white border border-gray-100 rounded-3xl p-5 md:p-6 shadow-lg space-y-6">
              
              {/* Syllabus header with progress */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-bangla text-base font-extrabold text-[#1F1F1F]">কোর্স সিলেবাস</h3>
                  <span className="font-bangla text-xs font-bold text-[#00C9A7] bg-[#00C9A7]/10 px-2 py-0.5 rounded-lg">
                    {progressPercentBn}% সম্পন্ন
                  </span>
                </div>
                
                {/* Progress bar */}
                <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-[#00C9A7] to-[#00A3FF] transition-all duration-300"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
                
                <div className="flex items-center justify-between text-[10px] font-bangla text-slate-400">
                  <span>সর্বমোট লেসন: {totalLessonsBn}টি</span>
                  <span>সম্পন্ন হয়েছে: {completedCountBn}টি</span>
                </div>
              </div>

              {/* Topics & Lessons Accordion list */}
              <div className="space-y-4 pt-2 border-t border-slate-100">
                {curriculum.length === 0 ? (
                  <p className="font-bangla text-xs text-slate-400 text-center py-4">এই কোর্সের সিলেবাস এখনো যুক্ত করা হয়নি।</p>
                ) : (
                  curriculum.map((topic: any, tIdx: number) => {
                    const topicKey = topic.id || topic.title;
                    const isExpanded = expandedTopics.has(topicKey);
                    const topicLessons = topic.lessons || [];
                    const topicLessonsCountBn = toBengaliNumber(topicLessons.length);
                    
                    return (
                      <div key={tIdx} className="bg-slate-50/50 rounded-2xl border border-slate-100 overflow-hidden">
                        {/* Topic Header Toggle */}
                        <button
                          onClick={() => toggleTopicAccordion(topicKey)}
                          className="w-full flex items-center justify-between p-4 text-left font-bangla text-sm font-bold text-slate-800 hover:bg-slate-50 cursor-pointer border-none bg-transparent"
                        >
                          <div className="flex flex-col text-left gap-0.5 w-full pr-2">
                            <span className="text-xs font-black text-slate-800 line-clamp-2 leading-tight">{topic.title}</span>
                            <span className="text-[10px] text-slate-400 font-medium mt-0.5">{topicLessonsCountBn}টি লেসন</span>
                          </div>
                          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`} />
                        </button>

                        {/* Lessons inside topic */}
                        {isExpanded && (
                          <div className="px-4 pb-4 pt-1 bg-white border-t border-slate-50">
                            <div className="relative pl-4 space-y-4 border-l border-slate-100 mt-2">
                              {topicLessons.length === 0 ? (
                                <p className="font-bangla text-[11px] text-slate-400 italic">কোনো লেসন নেই</p>
                              ) : (
                                topicLessons.map((lesson: any, lIdx: number) => {
                                  const lessonKey = lesson.id || lesson.title;
                                  const isActive = activeLesson?.id === lesson.id || activeLesson?.title === lesson.title;
                                  const isCompleted = completedLessons.has(lessonKey);

                                  return (
                                    <div 
                                      key={lIdx}
                                      onClick={() => {
                                        setActiveLesson(lesson);
                                        setActiveTopic(topic);
                                      }}
                                      className={`group flex items-start gap-3 cursor-pointer text-left relative transition-colors ${
                                        isActive ? 'text-[#00C9A7]' : 'text-slate-600 hover:text-[#00C9A7]'
                                      }`}
                                    >
                                      {/* Timeline Circle Node */}
                                      <div className={`absolute -left-[23px] top-1.5 w-[14px] h-[14px] rounded-full flex items-center justify-center z-10 transition-colors ${
                                        isCompleted 
                                          ? 'bg-[#00C9A7] text-white border border-[#00C9A7]' 
                                          : isActive 
                                            ? 'bg-white border-2 border-[#00C9A7]'
                                            : 'bg-white border border-slate-200 group-hover:border-[#00C9A7]'
                                      }`}>
                                        {isCompleted && (
                                          <Check className="w-2.5 h-2.5 stroke-[4.5]" />
                                        )}
                                        {isActive && !isCompleted && (
                                          <div className="w-1.5 h-1.5 rounded-full bg-[#00C9A7]" />
                                        )}
                                      </div>

                                      {/* Lesson Icon */}
                                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
                                        isActive 
                                          ? 'bg-[#00C9A7]/10 text-[#00C9A7]' 
                                          : 'bg-slate-50 text-slate-400 group-hover:bg-[#00C9A7]/5 group-hover:text-[#00C9A7]'
                                      }`}>
                                        {lesson.videoUrl ? (
                                          <Play className="w-3.5 h-3.5 fill-current" />
                                        ) : (
                                          <FileText className="w-3.5 h-3.5" />
                                        )}
                                      </div>

                                      {/* Lesson Title */}
                                      <div className="flex-1 min-w-0">
                                        <div className={`font-bangla text-xs font-bold transition-colors leading-relaxed ${
                                          isActive ? 'text-[#00C9A7] font-extrabold' : 'text-slate-700'
                                        }`}>
                                          {lesson.title}
                                        </div>
                                        {lesson.duration && (
                                          <div className="font-bangla text-[9px] text-slate-400 mt-0.5">
                                            সময়কাল: {toBengaliNumber(lesson.duration)}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

            </div>

          </div>
        </div>
      </div>
    );
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      {/* Top Header & Filters (Only visible in Gallery View) */}
      {!selectedCourseId && (
        <>
          <motion.div variants={itemVariants} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="font-bangla text-2xl font-bold text-[#1F1F1F]">কোর্সসমূহ</h2>
              <p className="font-bangla text-sm text-gray-500 mt-0.5">আপনার কাঙ্ক্ষিত ক্লাস ক্যাটাগরি নির্বাচন করে কোর্সগুলো ফিল্টার করুন</p>
            </div>
          </motion.div>

          {/* Main Category Filter Row */}
          <motion.div variants={itemVariants} className="grid grid-cols-2 md:grid-cols-4 gap-4 w-full">
            {categories.map((cat) => (
              <CategoryButton
                key={cat.id}
                cat={cat}
                active={activeCategory === cat.id}
                onClick={() => {
                  setActiveCategory(cat.id);
                  setSelectedCourseId(null);
                }}
              />
            ))}
          </motion.div>
        </>
      )}

      {isLoading ? (
        <SkeletonLoader />
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-3xl p-6 text-center space-y-2">
          <p className="font-bangla text-red-600 font-semibold">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-red-600 text-white rounded-xl text-xs font-bangla"
          >
            আবার চেষ্টা করুন
          </button>
        </div>
      ) : (
        <AnimatePresence mode="wait">
          {selectedCourseId && selectedCourse ? (
            isStudyMode ? (
              renderStudyView()
            ) : (
              /* ======================================================== */
              /* SINGLE COURSE VIEW LAYOUT (MATCHING MOCKUP ACCURATELY)   */
              /* ======================================================== */
              <motion.div
                key="single-course"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15 }}
                transition={{ duration: 0.3 }}
                className="space-y-8 bg-[#F4F2FF] dark:bg-[#0f172a] -mx-4 md:-mx-8 px-4 md:px-8 py-6 rounded-3xl pb-32 min-h-screen relative text-slate-800 dark:text-slate-100"
              >
            {(() => {
              const regularPriceVal = selectedCourse.regularPrice > 0 ? selectedCourse.regularPrice : 1929;
              const salePriceVal = selectedCourse.price > 0 ? selectedCourse.price : 12;
              const discountVal = selectedCourse.regularPrice > 0 && selectedCourse.price > 0 && selectedCourse.regularPrice > selectedCourse.price
                ? Math.round(((selectedCourse.regularPrice - selectedCourse.price) / selectedCourse.regularPrice) * 100)
                : 99;

              const regularPriceBn = toBengaliNumber(regularPriceVal.toLocaleString('en-US'));
              const salePriceBn = toBengaliNumber(salePriceVal.toLocaleString('en-US'));
              const discountBn = toBengaliNumber(discountVal);

              const promoVideoUrl = selectedCourse.promo_video || '';
              const embedUrl = getEmbedUrl(promoVideoUrl);

              const freeLessons = (selectedCourse.curriculum || []).reduce((acc: any[], topic: any) => {
                const lessons = topic.lessons || [];
                const freeInTopic = lessons.filter((l: any) => l.isFreePreview === true || l.isFreePreview === 'yes' || l.isFreePreview === 1);
                const mappedFree = freeInTopic.map((l: any) => ({
                  title: l.title,
                  videoUrl: l.videoUrl,
                  duration: l.duration || '১৫ মিনিট',
                  tutor: topic.title || 'বেসিক ক্লাস',
                }));
                return [...acc, ...mappedFree];
              }, []);

              const demoVideosToRender = freeLessons.length > 0 
                ? freeLessons 
                : (promoVideoUrl 
                    ? [{ title: `${selectedCourse.title} - ফ্রী ডেমো ক্লাস`, videoUrl: promoVideoUrl, duration: selectedCourse.duration || '১ ঘণ্টা', tutor: 'শুদ্ধশিক্ষা প্রমোশনাল' }]
                    : [
                        { title: `${selectedCourse.title} - বেসিক কনসেপ্ট ক্লাস`, duration: '৪৫ মিনিট', tutor: 'ইমরান হোসেন (BUET)', videoUrl: '' },
                        { title: `${selectedCourse.title} - সৃজনশীল ও জটিল ম্যাথ সমাধান ক্লাস`, duration: '৬০ মিনিট', tutor: 'মোঃ ইউসুফ (BUET)', videoUrl: '' }
                      ]
                  );

              return (
                <>
                  {/* Back to Gallery Link */}
                  <button 
                    onClick={() => {
                      if (window.history.state && window.history.state.selectedCourseId !== undefined) {
                        window.history.back();
                      } else {
                        setSelectedCourseId(null);
                      }
                    }}
                    className="flex items-center gap-2 text-sm text-[#00CBA9] dark:text-[#8AB4F8] font-bangla font-semibold hover:underline cursor-pointer bg-transparent border-none"
                  >
                    ← কোর্স গ্যালারিতে ফিরে যান
                  </button>

                  {/* Mobile-only Unified Promo Video & Features Card (Position 1) */}
                  <div className="block lg:hidden w-full bg-white rounded-3xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.03)] border border-slate-100/50 mb-6">
                    <div className="w-full aspect-video bg-slate-950 relative overflow-hidden">
                      {isPlayingVideo && promoVideoUrl ? (
                        embedUrl ? (
                          <iframe 
                            src={embedUrl} 
                            className="absolute inset-0 w-full h-full border-0" 
                            allow="autoplay; fullscreen" 
                            allowFullScreen 
                          />
                        ) : (
                          <video 
                            src={promoVideoUrl} 
                            className="absolute inset-0 w-full h-full object-cover" 
                            controls 
                            autoPlay 
                          />
                        )
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center">
                          {selectedCourse.thumbnail && !selectedCourse.thumbnail.includes("544716278-ca5e3f4abd8c") ? (
                            <img 
                              src={selectedCourse.thumbnail} 
                              alt={selectedCourse.title} 
                              className="absolute inset-0 w-full h-full object-cover opacity-80"
                            />
                          ) : (
                            <div className="absolute inset-0 bg-slate-900" />
                          )}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                          <button 
                            onClick={() => {
                              if (promoVideoUrl) {
                                setIsPlayingVideo(true);
                              } else {
                                triggerToast("এই কোর্সের প্রোমো ভিডিওটি খুব শীঘ্রই যুক্ত করা হবে!");
                              }
                            }}
                            className="w-16 h-16 rounded-full bg-white/30 backdrop-blur-md border border-white/50 flex items-center justify-center hover:scale-105 active:scale-95 transition-all cursor-pointer z-20 shadow-lg border-0"
                          >
                            <Play className="w-7 h-7 fill-white text-white translate-x-0.5" />
                          </button>
                          <span className="absolute bottom-4 right-4 text-xs text-white/90 font-bangla z-20 bg-black/40 px-3 py-1 rounded-lg">প্রোমো ভিডিও</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Mobile features list inside the video card */}
                    <div className="p-6 space-y-5">
                      <h3 className="font-bangla text-base font-extrabold text-[#1F1F1F]">এই কোর্সে যা যা অন্তর্ভুক্ত:</h3>
                      <div className="grid grid-cols-1 gap-4">
                        {[
                          { text: '৯০+ ইন্টারেক্টিভ লাইভ ক্লাস', icon: Play },
                          { text: '৩০+ লেকচার শিট ও ই-বুক', icon: BookOpen },
                          { text: '১৫টি অধ্যায়ভিত্তিক সাপ্তাহিক পরীক্ষা', icon: FileText },
                          { text: '২৪/৭ ডাউট সলভ ও গ্রুপ অ্যাক্সেস', icon: HelpCircle },
                          { text: 'কোর্স শেষে সার্টিফিকেট ও মেন্টরশীপ', icon: Star },
                        ].map((item, idx) => {
                          const Icon = item.icon;
                          return (
                            <div key={idx} className="flex items-center gap-3.5 text-sm text-slate-700 dark:text-slate-300 font-bangla font-medium">
                              <div className="w-8 h-8 rounded-lg bg-[#00C9A7]/10 flex items-center justify-center flex-shrink-0">
                                <Icon className="w-4.5 h-4.5 text-[#00C9A7]" strokeWidth={2.5} />
                              </div>
                              <span>{item.text}</span>
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Hotline Details inside mobile features stack */}
                      <div className="border-t border-slate-100 pt-4 text-center mt-2">
                        <p className="font-bangla text-[10px] text-slate-400">ভর্তি সংক্রান্ত যেকোনো তথ্যের জন্য</p>
                        <a href="tel:01712345678" className="font-english text-base font-extrabold text-[#1F1F1F] hover:text-[#00C9A7] transition-colors mt-0.5 inline-flex items-center gap-1.5 justify-center no-underline">
                          <Phone className="w-4 h-4 text-[#00C9A7] fill-current" /> 01712345678
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Course Landing Layout: 2 Columns */}
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
              
              {/* Left Column (Spans 8 cols) */}
              <div className="lg:col-span-8 space-y-8">
                
                {/* Hero Box / Overview Card */}
                <div className="bg-slate-900 text-white rounded-3xl p-6 md:p-8 space-y-4 shadow-xl relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-[#00CBA9]/15 rounded-full blur-3xl -z-10 translate-x-1/4 -translate-y-1/4" />
                  
                  <span className="inline-block px-3 py-1 bg-[#00CBA9] text-white font-bangla text-xs font-semibold rounded-full">
                    {selectedCourse.subtitle} • {selectedCourse.category}
                    {isFeatureActive('pageFeatureAuthor', false) && ` • ${selectedCourse.instructor || 'শিক্ষক'}`}
                  </span>
                  
                  <h1 className="font-bangla text-xl md:text-3xl font-extrabold leading-tight text-white">
                    {selectedCourse.title} অনলাইন কোর্স ও স্পেশাল প্রস্তুতি ব্যাচ
                  </h1>
                  
                  {isFeatureActive('pageFeatureAbout', true) && (
                    <p className="font-bangla text-sm text-slate-300 leading-relaxed max-w-2xl">
                      সম্পূর্ণ নতুন পরীক্ষা পদ্ধতি ও কারিকুলাম অনুযায়ী সাজানো এই ব্যাচে থাকবে প্রতিটি বিষয়ের পুঙ্খানুপুঙ্খ থিওরি আলোচনা, লাইভ সৃজনশীল সমাধান ক্লাস এবং হ্যান্ডনোট পিডিএফ সুবিধা।
                    </p>
                  )}

                  {isFeatureActive('pageFeatureBenefits', true) && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-3 text-slate-300 font-bangla text-xs border-t border-slate-800/80">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-[#00CBA9]" />
                        <span>সাপ্তাহিক ৩টি লাইভ ক্লাস</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-[#00A3FF]" />
                        <span>অধ্যায়ভিত্তিক মূল্যায়ন পরীক্ষা</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-yellow-400" />
                        <span>২৪/৭ মেন্টর ডাউট সলভ</span>
                      </div>
                    </div>
                  )}
                </div>



                {/* Banner Promo Card */}
                {isFeatureActive('pageFeatureAnnouncements', true) && (
                  <div className="bg-gradient-to-r from-red-950 via-rose-900 to-red-950 border border-rose-900/40 rounded-3xl p-6 flex flex-col md:flex-row items-center justify-between gap-6 shadow-md relative overflow-hidden">
                    <div className="space-y-2 text-center md:text-left z-10">
                      <h3 className="font-bangla text-lg md:text-xl font-extrabold text-white">আজই ব্যাচে ভর্তি হয়ে প্রস্তুতি ১০০% সম্পূর্ণ করো!</h3>
                      <p className="font-bangla text-xs text-rose-200">১ম ২০% মেম্বারদের জন্য বিশেষ ডিসকাউন্ট পিরিয়ড চলছে</p>
                    </div>
                    <button 
                      onClick={() => {
                        if (enrolledCourseIds.has(selectedCourse.id)) {
                          triggerToast("আপনি ইতিমধ্যে এই কোর্সে ভর্তি আছেন!");
                        } else {
                          setShowEnrollModal(true);
                        }
                      }}
                      className="px-6 py-3 bg-[#00CBA9] hover:bg-[#00b898] text-white font-bangla text-sm font-bold rounded-xl transition-all cursor-pointer shadow-md shadow-[#00CBA9]/20 z-10 animate-pulse"
                    >
                      {enrolledCourseIds.has(selectedCourse.id) ? 'পড়াশোনা শুরু করুন' : 'আজই যোগ দিন'}
                    </button>
                    <div className="absolute right-0 bottom-0 w-32 h-32 bg-rose-500/10 rounded-full blur-2xl" />
                  </div>
                )}

                {/* Course Instructors Grid Block */}
                {isFeatureActive('pageFeatureInstructorInfo', false) && (
                  <div className="bg-white rounded-3xl border border-gray-100 p-6 space-y-4 shadow-sm">
                    <div>
                      <h3 className="font-bangla text-lg font-bold text-gray-900">কোর্স ইনস্ট্রাক্টর</h3>
                      <p className="font-bangla text-xs text-gray-400 mt-1">এই কোর্সের পাঠ্যক্রম পরিচালনায় যুক্ত আছেন আমাদের দক্ষ শিক্ষকবৃন্দ</p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {getInstructorsForCourse(selectedCourse.title, selectedCourse.instructor).map((inst, idx) => (
                        <div 
                          key={idx} 
                          onClick={() => setSelectedInstructor(inst.name)}
                          className="flex items-center gap-4 p-4 bg-white border border-gray-100/80 rounded-2xl hover:border-[#00CBA9]/30 hover:shadow-sm transition-all cursor-pointer group"
                        >
                          {inst.avatar ? (
                            <img 
                              src={inst.avatar} 
                              alt={inst.name} 
                              className="w-14 h-14 rounded-full border border-gray-100 object-cover flex-shrink-0 group-hover:scale-105 transition-transform" 
                            />
                          ) : (
                            <div className={`w-14 h-14 rounded-full border border-gray-100 flex items-center justify-center font-bangla font-black text-xl shadow-inner flex-shrink-0 group-hover:scale-105 transition-transform ${
                              idx === 0 ? "bg-purple-50 text-purple-500" :
                              idx === 1 ? "bg-rose-50 text-rose-500" :
                              idx === 2 ? "bg-emerald-50 text-emerald-500" :
                              "bg-amber-50 text-amber-500"
                            }`}>
                              {inst.name.charAt(0)}
                            </div>
                          )}
                          <div className="space-y-0.5">
                            <h4 className="font-bangla text-sm font-extrabold text-[#1F1F1F] group-hover:text-[#00CBA9] transition-colors leading-snug">
                              {inst.name}
                            </h4>
                            <div className="text-[10px] font-english font-bold text-[#00CBA9] bg-emerald-50 dark:bg-emerald-950/20 px-2 py-0.5 rounded-full w-fit leading-none">
                              {inst.score}
                            </div>
                            <p className="font-bangla text-xs text-slate-400 leading-normal">
                              {inst.role} ({inst.exp})
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Class Routine Block */}
                <div className="bg-white rounded-3xl border border-gray-100 p-6 space-y-4 shadow-sm">
                  <h3 className="font-bangla text-lg font-bold text-gray-900 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-[#00CBA9]" />
                    ক্লাস রুটিন ও সময়সূচী
                  </h3>
                  
                  <div className="divide-y divide-gray-50 border border-gray-50 rounded-2xl overflow-hidden text-xs">
                    {[
                      { day: 'রবিবার', time: '৭:০০ PM - ৮:৩০ PM', topic: `${selectedCourse.title} - প্রথম লাইভ লেকচার ও থিওরি ক্লাস` },
                      { day: 'মঙ্গলবার', time: '৭:০০ PM - ৮:৩০ PM', topic: `${selectedCourse.title} - দ্বিতীয় লাইভ লেকচার ও প্র্যাকটিস সলভ` },
                      { day: 'বৃহস্পতিবার', time: '৭:০০ PM - ৮:৩০ PM', topic: `${selectedCourse.title} - সৃজনশীল ও এমসিকিউ পরীক্ষা সমাধান ক্লাস` },
                    ].map((row, idx) => (
                      <div key={idx} className="flex flex-col sm:flex-row sm:items-center p-4 gap-2 hover:bg-gray-50/50 transition-colors">
                        <div className="w-24 font-bangla font-bold text-[#1F1F1F] flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                          {row.day}
                        </div>
                        <div className="w-40 font-english font-semibold text-gray-500">{row.time}</div>
                        <div className="flex-1 font-bangla text-gray-600 font-medium">{row.topic}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Subject Syllabus Chapters Accordion */}
                {isFeatureActive('pageFeatureDescription', true) && (
                  <div className="bg-white rounded-3xl border border-gray-100 p-6 space-y-4 shadow-sm">
                    <div>
                      <h3 className="font-bangla text-lg font-bold text-gray-900">কোর্সের বিষয়ভিত্তিক সিলেবাস</h3>
                      <p className="font-bangla text-xs text-gray-400 mt-1">সবগুলো অধ্যায় ও তার বিস্তারিত আলোচনা মডিউল</p>
                    </div>

                    <div className="space-y-3">
                      {getSyllabus(selectedCourse.title).map((chapter, idx) => (
                        <div key={idx} className="bg-white rounded-2xl border border-gray-100 overflow-hidden transition-all duration-200">
                          <button 
                            onClick={() => toggleChapter(idx)}
                            className="w-full flex items-center justify-between p-4 text-left font-bangla text-sm font-bold text-[#1F1F1F] hover:bg-gray-50/50 cursor-pointer"
                          >
                            <span className="flex items-center gap-2">
                              <span className="w-5 h-5 rounded-full bg-[#00CBA9]/10 text-[#00CBA9] flex items-center justify-center text-[10px] font-bold">
                                {idx + 1}
                              </span>
                              {chapter.name}
                            </span>
                            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${openChapter === idx ? 'rotate-180' : ''}`} />
                          </button>
                          <AnimatePresence initial={false}>
                            {openChapter === idx && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                              >
                                <div className="px-5 pb-5 pt-1 text-xs text-gray-500 font-bangla leading-relaxed border-t border-gray-50/50">
                                  {chapter.desc}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Included Study Materials / Book Promos */}
                {isFeatureActive('pageFeatureMaterial', true) && (
                  <div className="space-y-4">
                    <h3 className="font-bangla text-lg font-bold text-gray-900 px-1">কোর্সের সাথে সম্পূর্ণ ফ্রী স্টাডি বুকস</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                      {[
                        { name: 'MasterBook', desc: 'লেকচার শিট ও টপিকওয়াইজ থিওরি নোট', color: 'from-pink-500 to-rose-600' },
                        { name: 'SuperSolve', desc: 'বিগত ১০ বছরের বোর্ড সৃজনশীল অংক সমাধান', color: 'from-slate-800 to-slate-900' },
                        { name: 'SuperPrep', desc: 'পরীক্ষার আগের রাতের স্পেশাল রিভিশন বুকলেট', color: 'from-blue-600 to-indigo-700' },
                      ].map((book, idx) => (
                        <div key={idx} className="bg-white rounded-2xl border border-gray-100 p-4 flex flex-col justify-between items-center text-center gap-4 hover:shadow-sm transition-shadow">
                          <div className={`w-28 h-36 bg-gradient-to-br ${book.color} rounded-xl shadow-md flex flex-col justify-between p-3 text-white relative overflow-hidden group-hover:scale-105 transition-transform`}>
                            <div className="absolute top-0 right-0 w-16 h-16 bg-white/10 rounded-full blur-lg" />
                            <span className="text-[7px] bg-white/20 px-1.5 py-0.5 rounded-full self-start">E-BOOK</span>
                            <span className="font-english font-extrabold text-sm tracking-wide text-center">{book.name}</span>
                            <span className="text-[7px] text-yellow-300 font-bangla font-semibold self-center">শুদ্ধশিক্ষা পাবলিকেশন</span>
                          </div>
                          <div>
                            <h5 className="font-bangla text-sm font-bold text-gray-900">{book.name}</h5>
                            <p className="font-bangla text-[10px] text-gray-400 mt-1 max-w-[150px] mx-auto leading-normal">{book.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Demo Classes Videos */}
                <div className="space-y-4">
                  <h3 className="font-bangla text-lg font-bold text-gray-900 px-1">ফ্রি ডেমো ক্লাস দেখে নিন</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {demoVideosToRender.map((demo: { title: string; videoUrl: string; thumbnail?: string; duration?: string; tutor?: string }, idx: number) => (
                      <div key={idx} className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm flex flex-col justify-between hover:shadow-md transition-shadow">
                        <div className="h-44 bg-slate-900 flex items-center justify-center text-white relative group">
                          {/* Video Thumbnail Background */}
                          <div className="absolute inset-0 bg-gradient-to-br from-purple-900/10 to-black/40 z-[1]" />
                          
                          <button 
                            type="button"
                            onClick={() => {
                              if (demo.videoUrl) {
                                setActiveDemoVideoUrl(demo.videoUrl);
                              } else {
                                triggerToast("ডেমো ক্লাস ভিডিও খুব শীঘ্রই যুক্ত করা হবে!");
                              }
                            }}
                            className="w-12 h-12 rounded-full bg-white/20 backdrop-blur border border-white/40 flex items-center justify-center hover:scale-110 transition-transform cursor-pointer z-10 hover:bg-[#00C9A7]/20 hover:border-[#00C9A7]"
                          >
                            <Play className="w-5 h-5 fill-white text-white translate-x-0.5" />
                          </button>
                          <span className="absolute bottom-3 left-3 text-[10px] bg-black/60 px-2 py-0.5 rounded text-white/95 font-semibold z-10">
                            {demo.duration ? toBengaliNumber(demo.duration) : ''}
                          </span>
                        </div>
                        <div className="p-4 space-y-1">
                          <h5 className="font-bangla text-xs font-bold text-gray-900 leading-snug">{demo.title}</h5>
                          <p className="font-bangla text-[10px] text-gray-400">{demo.tutor}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Who is this for Section */}
                {(isFeatureActive('pageFeatureTargetAudience', true) || isFeatureActive('pageFeatureRequirements', true)) && (
                  <div className="bg-[#FAF9FF] rounded-3xl border border-purple-50 p-6 space-y-4">
                    <h3 className="font-bangla text-base font-bold text-gray-900">কোর্সটি কাদের জন্য এবং কি কি লাগবে?</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {isFeatureActive('pageFeatureTargetAudience', true) && (
                        <div className="space-y-2">
                          <h4 className="font-bangla text-sm font-bold text-purple-900">কোর্সটি যাদের জন্য:</h4>
                          {[
                            'যারা ২০২৬ ও ২০২৭ সালের এসএসসি পরীক্ষার্থী',
                            'যাদের বেসিক দুর্বল এবং গোড়া থেকে বুঝতে চায়',
                            'সৃজনশীল প্রশ্নের সম্পূর্ণ উত্তর সাজাতে পারে না',
                          ].map((item, idx) => (
                            <div key={idx} className="flex items-start gap-2 text-xs font-bangla text-gray-600">
                              <div className="w-4 h-4 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                                ✓
                              </div>
                              <span>{item}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {isFeatureActive('pageFeatureRequirements', true) && (
                        <div className="space-y-2">
                          <h4 className="font-bangla text-sm font-bold text-teal-900">যা যা লাগবে ক্লাস করতে:</h4>
                          {[
                            'একটি স্মার্টফোন অথবা ল্যাপটপ কম্পিউটার',
                            'মোটামুটি গতির ইন্টারনেট কানেকশন',
                            'মনোযোগ ও নিয়মিত প্র্যাকটিস করার ইচ্ছা',
                          ].map((item, idx) => (
                            <div key={idx} className="flex items-start gap-2 text-xs font-bangla text-gray-600">
                              <div className="w-4 h-4 rounded-full bg-teal-100 text-teal-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                                ✓
                              </div>
                              <span>{item}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}


              </div>

              {/* Right Column (Floating Widget - Spans 4 cols - hidden on mobile) */}
              <div className={`hidden lg:block lg:col-span-4 space-y-6 ${
                isFeatureActive('pageFeatureStickySidebar', false) ? 'lg:sticky lg:top-24' : ''
              }`}>
                
                {/* Floating Checkout Box */}
                <div className="bg-white border border-gray-100 rounded-3xl p-5 shadow-lg space-y-5 relative overflow-hidden">
                  {/* Banner Video Preview (supports live playback) */}
                  <div className="h-44 bg-slate-900 rounded-2xl flex items-center justify-center text-white relative overflow-hidden z-0">
                    {isPlayingVideo ? (
                      embedUrl ? (
                        <iframe 
                          src={embedUrl} 
                          className="absolute inset-0 w-full h-full border-0" 
                          allow="autoplay; fullscreen" 
                          allowFullScreen 
                        />
                      ) : (
                        <video 
                          src={promoVideoUrl} 
                          className="absolute inset-0 w-full h-full object-cover" 
                          controls 
                          autoPlay 
                        />
                      )
                    ) : (
                      <>
                        {selectedCourse.thumbnail && !selectedCourse.thumbnail.includes("544716278-ca5e3f4abd8c") && (
                          <img 
                            src={selectedCourse.thumbnail} 
                            alt={selectedCourse.title} 
                            className="absolute inset-0 w-full h-full object-cover z-0 opacity-80"
                          />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/55 to-transparent z-10" />
                        <button 
                          onClick={() => setIsPlayingVideo(true)}
                          className="w-12 h-12 rounded-full bg-white/30 backdrop-blur-md border border-white/50 flex items-center justify-center hover:scale-105 transition-all cursor-pointer z-20"
                        >
                          <Play className="w-5 h-5 fill-white text-white translate-x-0.5" />
                        </button>
                        <span className="absolute bottom-3 right-3 text-[10px] text-white/90 font-bangla z-20 bg-black/40 px-2 py-0.5 rounded">প্রোমো ভিডিও</span>
                      </>
                    )}
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-baseline gap-2">
                      <span className="font-bangla text-2xl font-black text-[#1F1F1F]">৳{selectedCourse.price}</span>
                      {selectedCourse.regularPrice > selectedCourse.price && (
                        <>
                          <span className="font-bangla text-xs text-gray-400 line-through">৳{selectedCourse.regularPrice}</span>
                          <span className="font-bangla text-[10px] bg-red-50 text-red-500 font-bold px-2 py-0.5 rounded-full">
                            {Math.round(((selectedCourse.regularPrice - selectedCourse.price) / selectedCourse.regularPrice) * 100)}% ছাড়
                          </span>
                        </>
                      )}
                    </div>
                    <p className="font-bangla text-[11px] text-red-500 font-semibold flex items-center gap-1">
                      ⚠️ আর মাত্র ২ দিন স্পেশাল ডিসকাউন্ট প্রযোজ্য!
                    </p>
                  </div>

                  <div className="flex gap-2">
                    <button 
                      onClick={() => {
                        if (enrolledCourseIds.has(selectedCourse.id)) {
                          setIsStudyMode(true);
                          setIsPlayingVideo(false);
                        } else {
                          setShowEnrollModal(true);
                        }
                      }}
                      className={`flex-1 py-3.5 text-white font-bangla text-sm font-bold rounded-2xl transition-all cursor-pointer shadow-lg flex items-center justify-center gap-2 border-0 ${
                        enrolledCourseIds.has(selectedCourse.id)
                          ? 'bg-[#00A3FF] hover:bg-[#008be5] shadow-[#00A3FF]/15'
                          : 'bg-[#00CBA9] hover:bg-[#00b898] shadow-[#00CBA9]/15'
                      }`}
                    >
                      {enrolledCourseIds.has(selectedCourse.id) ? (
                        <>
                          <Star className="w-4 h-4 fill-white text-white" />
                          পড়াশোনা শুরু করুন
                        </>
                      ) : (
                        'ভর্তি হোন (Enroll Now)'
                      )}
                    </button>
                    {isFeatureActive('pageFeatureWishlist', true) && (
                      <button
                        onClick={() => {
                          triggerToast("কোর্সটি আপনার উইশলিস্টে যুক্ত করা হয়েছে!");
                        }}
                        className="p-3.5 bg-slate-50 border border-slate-100 rounded-2xl text-slate-400 hover:text-red-500 hover:bg-red-50 hover:border-red-100 transition-all cursor-pointer flex items-center justify-center"
                        title="Wishlist"
                      >
                        <Heart className="w-5.5 h-5.5 fill-current" />
                      </button>
                    )}
                  </div>

                  <div className="space-y-3 border-t border-gray-50 pt-4">
                    <div className="font-bangla text-xs font-bold text-gray-900">এই কোর্সে যা যা অন্তর্ভুক্ত:</div>
                    {[
                      { text: '৯০+ ইন্টারেক্টিভ লাইভ ক্লাস', icon: Play },
                      { text: '৩০+ লেকচার শিট ও ই-বুক', icon: BookOpen },
                      { text: '১৫টি অধ্যায়ভিত্তিক সাপ্তাহিক পরীক্ষা', icon: FileText },
                      { text: '২৪/৭ ডাউট সলভ ও গ্রুপ অ্যাক্সেস', icon: HelpCircle },
                      { text: 'কোর্স শেষে সার্টিফিকেট ও মেন্টরশীপ', icon: Star },
                    ].map((item, idx) => {
                      const Icon = item.icon;
                      return (
                        <div key={idx} className="flex items-center gap-2.5 text-xs text-gray-600 font-bangla font-medium">
                          <Icon className="w-4 h-4 text-[#00CBA9] flex-shrink-0" />
                          <span>{item.text}</span>
                        </div>
                      );
                    })}

                    {/* Level */}
                    {isFeatureActive('pageFeatureLevel', true) && (
                      <div className="flex items-center gap-2.5 text-xs text-gray-600 font-bangla font-medium pt-0.5">
                        <Users className="w-4 h-4 text-[#00CBA9] flex-shrink-0" />
                        <span>কোর্সের স্তর: {selectedCourse.level || 'সকল স্তরের জন্য'}</span>
                      </div>
                    )}

                    {/* Duration */}
                    {isFeatureActive('pageFeatureDuration', true) && (
                      <div className="flex items-center gap-2.5 text-xs text-gray-600 font-bangla font-medium">
                        <Clock className="w-4 h-4 text-[#00CBA9] flex-shrink-0" />
                        <span>সময়কাল: {selectedCourse.duration || '১৬+ ঘণ্টা কনটেন্ট'}</span>
                      </div>
                    )}

                    {/* Total Enrolled */}
                    {isFeatureActive('pageFeatureTotalEnrolled', true) && (
                      <div className="flex items-center gap-2.5 text-xs text-gray-600 font-bangla font-medium">
                        <Users className="w-4 h-4 text-[#00CBA9] flex-shrink-0" />
                        <span>ভর্তি কৃত ছাত্র-ছাত্রী: {selectedCourse.studentsCount || '১,২০০+ জন'}</span>
                      </div>
                    )}

                    {/* Update Date */}
                    {isFeatureActive('pageFeatureUpdateDate', true) && (
                      <div className="flex items-center gap-2.5 text-xs text-gray-600 font-bangla font-medium">
                        <Calendar className="w-4 h-4 text-[#00CBA9] flex-shrink-0" />
                        <span>সর্বশেষ আপডেট: {selectedCourse.updateDate || 'জুন ২০২৬'}</span>
                      </div>
                    )}

                    {/* Social Share */}
                    {isFeatureActive('pageFeatureSocialShare', true) && (
                      <div className="flex items-center justify-center gap-3 pt-3 border-t border-gray-50">
                        <span className="text-[10px] text-gray-400 font-bangla">শেয়ার করুন:</span>
                        <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-blue-600 transition-colors">
                          <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c4.56-.93 8-4.96 8-9.75z"/></svg>
                        </a>
                        <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-sky-400 transition-colors">
                          <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/></svg>
                        </a>
                      </div>
                    )}
                  </div>

                  {/* Hotline */}
                  <div className="border-t border-gray-50 pt-4 text-center">
                    <p className="font-bangla text-[10px] text-gray-400">ভর্তি সংক্রান্ত যেকোনো তথ্যের জন্য</p>
                    <a href="tel:01712345678" className="font-english text-base font-extrabold text-[#1F1F1F] hover:text-[#00CBA9] transition-colors mt-0.5 inline-flex items-center gap-1.5 justify-center">
                      <Phone className="w-4 h-4 text-[#00CBA9] fill-current" /> 01712345678
                    </a>
                  </div>
                </div>

              </div>

            </div>

            {/* Sticky Bottom Bar for Mobile (Position 6) */}
            <div className="block lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white/85 backdrop-blur-xl border-t border-slate-100 shadow-[0_-8px_30px_rgb(0,0,0,0.05)] p-4 transition-all duration-300">
              <div className="max-w-md mx-auto flex items-center justify-between gap-4">
                {/* Left: Dynamic Price Layout */}
                <div className="flex flex-col flex-shrink-0">
                  <div className="flex items-baseline gap-1.5">
                    <span className="font-bangla text-2xl font-black text-slate-900">৳{salePriceBn}</span>
                    <span className="font-bangla text-xs text-slate-400 line-through">৳{regularPriceBn}</span>
                  </div>
                  <span className="self-start font-bangla text-[9px] font-extrabold bg-red-50 text-red-500 px-1.5 py-0.5 rounded-md mt-0.5 shadow-sm">
                    {discountBn}% ছাড়
                  </span>
                </div>
                
                {/* Right: Mint Green Button with Right Arrow */}
                <button 
                  onClick={() => {
                    if (enrolledCourseIds.has(selectedCourse.id)) {
                      setIsStudyMode(true);
                      setIsPlayingVideo(false);
                    } else {
                      setShowEnrollModal(true);
                    }
                  }}
                  className={`flex-1 py-3.5 text-white font-bangla text-sm font-extrabold rounded-2xl transition-all shadow-md flex items-center justify-center gap-2 cursor-pointer border-0 active:scale-95 group ${
                    enrolledCourseIds.has(selectedCourse.id)
                      ? 'bg-[#00A3FF] hover:bg-[#008be5] shadow-[#00A3FF]/15'
                      : 'bg-[#00C9A7] hover:bg-[#00b093] shadow-[#00C9A7]/15'
                  }`}
                >
                  <span>
                    {enrolledCourseIds.has(selectedCourse.id) ? 'পড়াশোনা শুরু করুন' : 'ভর্তি হোন (Enroll Now)'}
                  </span>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor" className="w-4 h-4 text-white transition-transform duration-200 group-hover:translate-x-1">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                  </svg>
                </button>
              </div>
            </div>
          </>
        );
      })()}
    </motion.div>
    )
        ) : (
          /* ======================================================== */
          /* MAIN GALLERY VIEW (SSC LANDING PAGE OR ACADEMIC GRID)    */
          /* ======================================================== */
          <motion.div
            key={`gallery-${activeCategory}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-6"
          >
            {/* If SSC: Show detailed layout */}
            {activeCategory === 'এসএসসি' ? (
              <div className="space-y-10">
                
                {/* Sub Group Toggles */}
                <div className="flex justify-center border-b border-gray-200/60 pb-4">
                  <div className="bg-white/80 backdrop-blur border border-gray-100 p-1.5 rounded-2xl flex flex-wrap gap-1 shadow-sm">
                    {[
                      { id: 'all', label: 'অল' },
                      { id: 'science', label: 'বিজ্ঞান' },
                      { id: 'commerce', label: 'ব্যবসায় শিক্ষা' },
                      { id: 'humanities', label: 'মানবিক' },
                    ].map((group) => (
                      <button
                        key={group.id}
                        onClick={() => setSscGroup(group.id as any)}
                        className={`font-bangla px-6 py-2.5 rounded-xl text-sm font-semibold transition-all cursor-pointer ${
                          sscGroup === group.id
                            ? 'bg-[#00CBA9] text-white shadow-md shadow-[#00CBA9]/20'
                            : 'text-gray-500 hover:text-[#1F1F1F] hover:bg-gray-50'
                        }`}
                      >
                        {group.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Groups list */}
                <div className="space-y-12">
                  {/* If there are no courses at all for SSC */}
                  {mappedCourses.filter(c => c.category === 'এসএসসি').length === 0 && (
                    <div className="text-center py-10">
                      <p className="font-bangla text-gray-400">এই ক্যাটাগরিতে কোনো курс পাওয়া যায়নি।</p>
                    </div>
                  )}

                  {/* General / Compulsory Courses */}
                  {(sscGroup === 'all') && (() => {
                    const generalCourses = mappedCourses.filter(c => c.category === 'এসএসসি' && c.group === 'all');
                    if (generalCourses.length === 0) return null;
                    return (
                      <div 
                        className="bg-white rounded-3xl border border-blue-50 p-6 md:p-8 space-y-6 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden"
                      >
                        <div className="absolute top-0 right-0 w-48 h-48 bg-blue-50/10 rounded-full blur-3xl -z-10 translate-x-12 -translate-y-12" />
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-blue-50 pb-5">
                          <div>
                            <h3 className="font-bangla text-xl font-bold text-blue-950 flex items-center gap-2">
                              <div className="w-2.5 h-2.5 rounded-full bg-blue-600" />
                              SSC 2027 (আবশ্যিক বিষয়সমূহ)
                            </h3>
                            <p className="font-bangla text-xs text-blue-600/80 mt-1">আবশ্যিক বিষয়সমূহের অনলাইন কোর্স</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                          {generalCourses.map(course => (
                            <CourseCard key={course.id} course={course} />
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Science */}
                  {(sscGroup === 'all' || sscGroup === 'science') && (() => {
                    const scienceCourses = mappedCourses.filter(c => c.category === 'এসএসসি' && c.group === 'science');
                    if (scienceCourses.length === 0) return null;
                    return (
                      <div 
                        className="bg-white rounded-3xl border border-purple-100 p-6 md:p-8 space-y-6 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden"
                      >
                        <div className="absolute top-0 right-0 w-48 h-48 bg-purple-50/50 rounded-full blur-3xl -z-10 translate-x-12 -translate-y-12" />
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-purple-50 pb-5">
                          <div>
                            <h3 className="font-bangla text-xl font-bold text-purple-950 flex items-center gap-2">
                              <div className="w-2.5 h-2.5 rounded-full bg-purple-600" />
                              SSC 2027 (বিজ্ঞান বিভাগ)
                            </h3>
                            <p className="font-bangla text-xs text-purple-600/80 mt-1">বিজ্ঞান বিভাগের অনলাইন কোর্স ও সলিউশন বান্ডেল</p>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {['পদার্থবিজ্ঞান', 'রসায়ন', 'উচ্চতর গণিত', 'জীববিজ্ঞান'].map((subject) => (
                            <div key={subject} className="bg-purple-50/60 text-purple-700 font-bangla font-semibold px-4 py-3 rounded-2xl border border-purple-100/50 flex items-center justify-center text-center text-sm">
                              {subject}
                            </div>
                          ))}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                          {scienceCourses.map(course => (
                            <CourseCard key={course.id} course={course} />
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Commerce */}
                  {(sscGroup === 'all' || sscGroup === 'commerce') && (() => {
                    const commerceCourses = mappedCourses.filter(c => c.category === 'এসএসসি' && c.group === 'commerce');
                    if (commerceCourses.length === 0) return null;
                    return (
                      <div 
                        className="bg-white rounded-3xl border border-blue-100 p-6 md:p-8 space-y-6 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden"
                      >
                        <div className="absolute top-0 right-0 w-48 h-48 bg-blue-50/50 rounded-full blur-3xl -z-10 translate-x-12 -translate-y-12" />
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-blue-50 pb-5">
                          <div>
                            <h3 className="font-bangla text-xl font-bold text-blue-950 flex items-center gap-2">
                              <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />
                              SSC 2027 (ব্যবসায় শিক্ষা)
                            </h3>
                            <p className="font-bangla text-xs text-blue-500/80 mt-1">ব্যবসায় শিক্ষা বিভাগের অনলাইন কোর্স ও সলিউশন বান্ডেল</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                          {commerceCourses.map(course => (
                            <CourseCard key={course.id} course={course} />
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Humanities */}
                  {(sscGroup === 'all' || sscGroup === 'humanities') && (() => {
                    const humanitiesCourses = mappedCourses.filter(c => c.category === 'এসএসসি' && c.group === 'humanities');
                    if (humanitiesCourses.length === 0) return null;
                    return (
                      <div 
                        className="bg-white rounded-3xl border border-amber-100 p-6 md:p-8 space-y-6 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden"
                      >
                        <div className="absolute top-0 right-0 w-48 h-48 bg-amber-50/50 rounded-full blur-3xl -z-10 translate-x-12 -translate-y-12" />
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-amber-50 pb-5">
                          <div>
                            <h3 className="font-bangla text-xl font-bold text-amber-950 flex items-center gap-2">
                              <div className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                              SSC 2027 (মানবিক বিভাগ)
                            </h3>
                            <p className="font-bangla text-xs text-amber-500/80 mt-1">মানবিক বিভাগের অনলাইন কোর্স ও সলিউশন বান্ডেল</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                          {humanitiesCourses.map(course => (
                            <CourseCard key={course.id} course={course} />
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* Benefits grid */}
                <div className="bg-[#EBF5F8]/70 border border-[#D0EBF3]/40 rounded-3xl p-6 md:p-8 space-y-6">
                  <div className="text-center space-y-2">
                    <h3 className="font-bangla text-xl md:text-2xl font-bold text-gray-900">কোর্সগুলো যেভাবে সাহায্য করবে</h3>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                      { title: 'লাইভ ক্লাস', desc: 'সরাসরি প্রশ্ন করার সুযোগ', icon: Play, color: 'bg-red-50 text-red-500' },
                      { title: 'রেকর্ডেড ক্লাস', desc: 'যে কোনো সময় রিভিশন দিন', icon: Clock, color: 'bg-blue-50 text-blue-500' },
                      { title: 'প্র্যাকটিস শিট', desc: 'চ্যাপ্টারভিত্তিক প্রশ্নব্যাংক', icon: BookOpen, color: 'bg-purple-50 text-purple-500' },
                      { title: 'উইকলি এক্সাম', desc: 'সাপ্তাহিক মূল্যায়ন পরীক্ষা', icon: FileText, color: 'bg-emerald-50 text-emerald-500' },
                    ].map((feat, i) => {
                      const Icon = feat.icon;
                      return (
                        <div key={i} className="bg-white p-4 rounded-2xl border border-gray-100 flex flex-col items-center text-center space-y-2">
                          <div className={`w-10 h-10 rounded-xl ${feat.color} flex items-center justify-center`}>
                            <Icon className="w-5 h-5" />
                          </div>
                          <h5 className="font-bangla text-sm font-bold text-gray-900">{feat.title}</h5>
                          <p className="font-bangla text-[10px] text-gray-400 leading-snug">{feat.desc}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Testimonials */}
                {isFeatureActive('pageFeatureReview', true) && (
                  <div className="space-y-6">
                    <div className="text-center">
                      <h3 className="font-bangla text-xl font-bold text-gray-900">অভিভাবক ও শিক্ষার্থীদের মতামত</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                      {[
                        { name: 'তাসনিয়া রহমান', role: '학생, SSC 24', color: 'from-purple-900 to-indigo-950', initial: 'তা' },
                        { name: 'ফারহান আহমেদ', role: '학생, SSC 24', color: 'from-blue-900 to-cyan-950', initial: 'ফা' },
                      ].map((review, i) => (
                        <div key={i} className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm relative group">
                          <div className={`h-36 bg-gradient-to-br ${review.color} flex items-center justify-center text-white`}>
                            <button className="w-10 h-10 rounded-full bg-white/20 backdrop-blur flex items-center justify-center cursor-pointer">
                              <Play className="w-4 h-4 fill-white text-white translate-x-0.5" />
                            </button>
                          </div>
                          <div className="p-3 flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-[10px] font-bold text-gray-600 font-bangla">
                              {review.initial}
                            </div>
                            <div>
                              <h5 className="font-bangla text-xs font-bold text-[#1F1F1F]">{review.name}</h5>
                              <p className="font-bangla text-[8px] text-gray-400">{review.role}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* FAQ */}
                {isFeatureActive('pageFeatureQA', false) && (
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-2 space-y-4">
                      <h3 className="font-bangla text-lg font-bold text-gray-900">সার্বিক তথ্যের জন্য সাধারণ প্রশ্নসমূহ</h3>
                      {[
                        { q: 'অনলাইন ব্যাচে ক্লাসগুলো কিভাবে নেওয়া হবে?', a: 'ক্লাসগুলো সম্পূর্ণ লাইভ ও ইন্টারেক্টিভ হবে। রেকর্ডেড ব্যাকআপও পাওয়া যাবে।' },
                        { q: 'পেমেন্ট কিভাবে করতে হবে?', a: 'বিকাশ, রকেট, নগদ বা কার্ডের মাধ্যমে পেমেন্ট করতে পারবেন।' }
                      ].map((faq, i) => (
                        <div key={i} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                          <button 
                            onClick={() => toggleFaq(i)}
                            className="w-full flex items-center justify-between p-4 text-left font-bangla text-sm font-bold text-[#1F1F1F] cursor-pointer"
                          >
                            <span>{faq.q}</span>
                            <ChevronDown className="w-4 h-4 text-gray-400" />
                          </button>
                          {openFaq === i && (
                            <div className="px-4 pb-4 pt-1 text-xs text-gray-500 font-bangla border-t border-gray-50">
                              {faq.a}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="bg-white border border-[#E0EBF3] rounded-3xl p-5 text-center flex flex-col justify-between items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-[#00CBA9]/10 text-[#00CBA9] flex items-center justify-center">
                        <Phone className="w-4 h-4 fill-current" />
                      </div>
                      <div className="space-y-1">
                        <h4 className="font-bangla text-sm font-bold text-gray-900">সরাসরি পরামর্শের জন্য</h4>
                        <p className="font-english text-lg font-black text-[#1F1F1F]">01712345678</p>
                      </div>
                      <a href="tel:01712345678" className="w-full py-2.5 bg-[#00CBA9] hover:bg-[#00b898] text-white font-bangla text-xs font-bold rounded-xl transition-all text-center">
                        কল দিন
                      </a>
                    </div>
                  </div>
                )}

              </div>
            ) : (
              /* ======================================================== */
              /* STANDARD FALLBACK GRID FOR OTHER ACADEMIC CLASSES (6-8) */
              /* ======================================================== */
              filteredClassCourses.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-3xl border border-slate-100 shadow-sm">
                  <div className="text-4xl mb-4">📚</div>
                  <p className="font-bangla text-gray-500 font-semibold">এই ক্যাটাগরিতে কোনো কোর্স পাওয়া যায়নি।</p>
                </div>
              ) : (
                <motion.div
                  key={activeCategory}
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                  className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
                >
                  {filteredClassCourses.map((course) => (
                    <motion.div key={course.id} variants={itemVariants}>
                      <CourseCard course={course} />
                    </motion.div>
                  ))}
                </motion.div>
              )
            )}
          </motion.div>
        )}
      </AnimatePresence>
      )}

      {/* ======================================================== */}
      {/* CHECKOUT PACKAGE SELECTION MODAL (MATCHING SCREENSHOT)   */}
      {/* ======================================================== */}
      <AnimatePresence>
        {showEnrollModal && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-3xl max-w-2xl w-full p-6 md:p-8 relative shadow-2xl border border-gray-100 flex flex-col gap-6"
            >
              {/* Close Button */}
              <button 
                onClick={() => setShowEnrollModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <X className="w-6 h-6" />
              </button>

              {enrollStep === 'select' ? (
                <>
                  {/* Modal Header */}
                  <div>
                    <h3 className="font-bangla text-lg md:text-xl font-bold text-slate-800">পছন্দমত প্যাকেজ বাছাই করুন</h3>
                    <p className="font-bangla text-xs text-gray-500 mt-1">আপনার প্যাকেজের সাথে অন্যরা যা কিনছে তা আপনিও কিনতে পারবেন।</p>
                  </div>

                  {/* Package Selectors Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    
                    {/* Option 1: 5-Day Free Trial */}
                    <div 
                      onClick={() => setSelectedPackage('trial')}
                      className={`border-2 rounded-2xl p-5 flex flex-col justify-between gap-4 cursor-pointer transition-all ${
                        selectedPackage === 'trial'
                          ? 'border-[#00CBA9] bg-[#00CBA9]/5 shadow-sm'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      <div className="flex justify-between items-start gap-2">
                        <div>
                          <h4 className="font-bangla text-sm md:text-base font-extrabold text-slate-800">৫ দিনের ফ্রি ট্রায়াল</h4>
                          <p className="font-bangla text-[10px] text-gray-400 mt-1">মেয়াদঃ 5 দিন • ভর্তি শেষ 31 ডিসেম্বর</p>
                        </div>
                        {/* Radio input indicator */}
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                          selectedPackage === 'trial' ? 'border-[#00CBA9]' : 'border-gray-300'
                        }`}>
                          {selectedPackage === 'trial' && <div className="w-2.5 h-2.5 rounded-full bg-[#00CBA9]" />}
                        </div>
                      </div>
                      
                      <div className="font-bangla text-2xl font-black text-[#00CBA9]">৳০</div>
                      
                      <div className="border-t border-gray-100 pt-3">
                        <p className="font-bangla text-[10px] text-gray-500 leading-relaxed bg-emerald-50/50 p-2.5 rounded-xl border border-emerald-100/30">
                          ৫ দিনের ফ্রি ট্রায়ালে শিক্ষার্থীরা অনলাইন ব্যাচের লাইভ ক্লাস, ডেইলি HW, পরীক্ষা, 24/7 AI Doubt Solver এবং রিভিশন সাপোর্টের অভিজ্ঞতা নিতে পারবে—একদম ফ্রি।
                        </p>
                      </div>
                    </div>

                    {/* Option 2: Full Package */}
                    <div 
                      onClick={() => setSelectedPackage('full')}
                      className={`border-2 rounded-2xl p-5 flex flex-col justify-between gap-4 cursor-pointer transition-all ${
                        selectedPackage === 'full'
                          ? 'border-[#00CBA9] bg-[#00CBA9]/5 shadow-sm'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      <div className="flex justify-between items-start gap-2">
                        <div>
                          <h4 className="font-bangla text-sm md:text-base font-extrabold text-slate-800">বাংলা, ইংরেজি ও আইসিটি</h4>
                          <p className="font-bangla text-[10px] text-gray-400 mt-1">মেয়াদঃ 18 Nov, 2025 to 30 Apr, 2027 • ভর্তি শেষ 30 এপ্রিল</p>
                        </div>
                        {/* Radio input indicator */}
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                          selectedPackage === 'full' ? 'border-[#00CBA9]' : 'border-gray-300'
                        }`}>
                          {selectedPackage === 'full' && <div className="w-2.5 h-2.5 rounded-full bg-[#00CBA9]" />}
                        </div>
                      </div>

                      <div className="flex items-baseline gap-2">
                        <span className="font-bangla text-2xl font-black text-[#1F1F1F]">৳{selectedCourse.price}</span>
                        {selectedCourse.regularPrice > selectedCourse.price && (
                          <>
                            <span className="font-bangla text-xs text-gray-400 line-through">৳{selectedCourse.regularPrice}</span>
                            <span className="font-bangla text-[9px] bg-emerald-100 text-emerald-700 font-bold px-2 py-0.5 rounded">
                              বাঁচবে ৳{selectedCourse.regularPrice - selectedCourse.price}
                            </span>
                          </>
                        )}
                      </div>

                      <div className="border-t border-gray-100 pt-3">
                        <p className="font-bangla text-[10px] text-gray-500 leading-relaxed p-2.5">
                          সম্পূর্ণ কোর্সের পূর্ণাঙ্গ অ্যাক্সেস এবং রেগুলার একাডেমিক মেন্টরশিপ সাপোর্ট।
                        </p>
                      </div>
                    </div>

                  </div>

                  {/* Proceed Button */}
                  <button 
                    onClick={handleEnrollProceed}
                    className="w-full sm:w-auto self-center bg-[#00CBA9] hover:bg-[#00b898] text-white font-bangla text-sm font-bold px-12 py-3.5 rounded-xl transition-all cursor-pointer shadow-lg shadow-[#00CBA9]/15"
                  >
                    এগিয়ে যান
                  </button>
                </>
              ) : (
                <>
                  {/* Step 2: Manual Payment Checkout View */}
                  <div className="flex items-center gap-3 border-b border-gray-100 pb-3 mb-2">
                    <button 
                      onClick={() => setEnrollStep('select')}
                      className="p-1.5 hover:bg-slate-50 rounded-full transition-all cursor-pointer text-gray-500 hover:text-gray-700 border-none bg-transparent"
                    >
                      <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div className="text-left">
                      <h3 className="font-bangla text-base md:text-lg font-bold text-slate-800">পেমেন্ট নিশ্চিত করুন</h3>
                      <p className="font-bangla text-[11px] text-gray-400">ম্যানুয়াল পেমেন্টের মাধ্যমে আপনার ভর্তি ফি সম্পন্ন করুন।</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block text-left">পেমেন্ট মাধ্যম সিলেক্ট করুন</label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {manualMethods.map(method => (
                          <button
                            key={method.id}
                            type="button"
                            onClick={() => setActivePaymentMethod(method.id)}
                            className={`flex items-center justify-center p-3 rounded-2xl border transition-all cursor-pointer relative border-solid ${
                              activePaymentMethod === method.id
                                ? 'border-[#00CBA9] bg-[#00CBA9]/5 text-[#00CBA9] font-bold'
                                : 'border-gray-200 bg-transparent hover:bg-gray-50 text-gray-600'
                            }`}
                          >
                            <span className="text-xs font-bangla">{method.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Dynamic Payment Instructions */}
                    <div className="space-y-3 bg-slate-50 border border-gray-100 p-4.5 rounded-2xl text-left">
                      <div className="text-[11px] md:text-xs text-gray-600 leading-relaxed font-bangla whitespace-pre-line">
                        {manualMethods.find(m => m.id === activePaymentMethod)?.instructions}
                      </div>
                      
                      {/* Payment Input Fields */}
                      <div className="space-y-3 pt-2.5 border-t border-gray-200/50">
                        <div className="space-y-1.5">
                          <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider font-bangla">সেন্ডার মোবাইল নম্বর</label>
                          <input
                            type="text"
                            placeholder="যেমন: ০১৭XXXXXXXX"
                            value={senderNumber}
                            onChange={(e) => setSenderNumber(e.target.value)}
                            className="w-full px-4 py-2.5 text-xs border border-gray-200 rounded-xl bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#00CBA9]/20 focus:border-[#00CBA9] font-bangla"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider font-bangla">ট্রানজেকশন আইডি (TxnID)</label>
                          <input
                            type="text"
                            placeholder="যেমন: K87JHG9"
                            value={transactionId}
                            onChange={(e) => setTransactionId(e.target.value)}
                            className="w-full px-4 py-2.5 text-xs border border-gray-200 rounded-xl bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#00CBA9]/20 focus:border-[#00CBA9]"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Checkout Submit Button */}
                    <button
                      onClick={handleConfirmManualPayment}
                      disabled={isSubmittingPayment}
                      className="w-full py-3.5 bg-[#00CBA9] hover:bg-[#00b898] text-white font-bangla font-bold text-center text-sm rounded-xl cursor-pointer shadow-lg shadow-[#00CBA9]/15 transition-all border-none disabled:opacity-50"
                    >
                      {isSubmittingPayment ? 'অর্ডার সাবমিট হচ্ছে...' : 'পেমেন্ট নিশ্চিত করুন'}
                    </button>
                  </div>
                </>
              )}

            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ======================================================== */}
      {/* FREE DEMO VIDEO POPUP PLAYER MODAL                       */}
      {/* ======================================================== */}
      <AnimatePresence>
        {activeDemoVideoUrl && (
          <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-slate-950 rounded-2xl max-w-4xl w-full aspect-video relative shadow-2xl border border-white/10 overflow-hidden"
            >
              {/* Close Button */}
              <button 
                type="button"
                onClick={() => setActiveDemoVideoUrl(null)}
                className="absolute top-4 right-4 bg-black/60 hover:bg-black/90 text-white rounded-full p-2 z-10 hover:scale-105 transition-transform cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>

              {(() => {
                const embedUrl = getEmbedUrl(activeDemoVideoUrl);
                if (embedUrl) {
                  return (
                    <iframe
                      src={embedUrl}
                      className="w-full h-full"
                      allowFullScreen
                      allow="autoplay; encrypted-media"
                    />
                  );
                }
                return (
                  <video 
                    src={activeDemoVideoUrl} 
                    controls 
                    autoPlay
                    className="w-full h-full object-contain" 
                  />
                );
              })()}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Floating Success Notification Toast */}
      <AnimatePresence>
        {showSuccessToast && (
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-6 right-6 z-50 bg-[#1F1F1F] text-white px-5 py-4 rounded-2xl shadow-xl flex items-center gap-3 border border-slate-800"
          >
            <div className="w-6 h-6 rounded-full bg-[#00CBA9] flex items-center justify-center flex-shrink-0">
              <Check className="w-3.5 h-3.5 text-white" />
            </div>
            <div className="font-bangla text-xs font-semibold">
              {toastMessage}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Instructor Public Profile Modal */}
      <AnimatePresence>
        {selectedInstructor && (() => {
          const name = selectedInstructor;
          // Filter other courses by this instructor
          const otherCourses = mappedCourses.filter(c => c && c.instructor === name);
          
          // Find database instructor details
          const dbInstructor = publicInstructors.find(i => i.name === name);
          const designation = dbInstructor ? dbInstructor.designation : "মাস্টার ট্রেইনার, শুদ্ধশিক্ষা";
          const rating = dbInstructor ? dbInstructor.rating : "৫.০";
          const reviewCount = dbInstructor ? dbInstructor.reviewCount : "১২টি রিভিউ";
          const studentsCount = dbInstructor ? dbInstructor.studentsCount : "১,২০০+ জন";
          const experience = dbInstructor ? dbInstructor.experience : "১.৫+ Years Exp";
          const bio = dbInstructor ? dbInstructor.bio : "";
          const facebook = dbInstructor && dbInstructor.facebook && dbInstructor.facebook !== '#' ? dbInstructor.facebook : "https://facebook.com";
          const youtube = dbInstructor && dbInstructor.youtube && dbInstructor.youtube !== '#' ? dbInstructor.youtube : "https://youtube.com";
          const linkedin = dbInstructor && dbInstructor.linkedin && dbInstructor.linkedin !== '#' ? dbInstructor.linkedin : "https://linkedin.com";
          const avatarUrl = dbInstructor && dbInstructor.avatar ? dbInstructor.avatar : null;

          return (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm overflow-y-auto">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="bg-white dark:bg-slate-900 rounded-[32px] w-full max-w-4xl shadow-2xl overflow-hidden border border-slate-100 dark:border-slate-800 relative my-8"
              >
                {/* Close Button */}
                <button
                  onClick={() => setSelectedInstructor(null)}
                  className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full bg-black/20 hover:bg-black/40 text-white flex items-center justify-center border-none cursor-pointer backdrop-blur-md transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
                
                {/* Banner / Cover image */}
                <div className="h-48 md:h-56 bg-gradient-to-r from-indigo-500 via-purple-500 to-[#00C9A7] relative flex items-end">
                  <div className="absolute inset-0 bg-black/10" />
                  {/* Decorative shapes */}
                  <div className="absolute top-6 right-8 w-16 h-16 rounded-full bg-white/10 blur-xl" />
                  <div className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full bg-[#00C9A7]/20 blur-2xl" />
                </div>

                {/* Profile Header Block */}
                <div className="px-6 md:px-10 pb-6 relative">
                  {/* Avatar overlap */}
                  <div className="flex flex-col md:flex-row items-start md:items-end gap-5 -mt-16 mb-6">
                    <div className="w-28 h-28 md:w-32 md:h-32 rounded-full bg-white dark:bg-slate-900 p-1.5 shadow-xl flex-shrink-0">
                      {avatarUrl ? (
                        <img 
                          src={avatarUrl} 
                          alt={name} 
                          className="w-full h-full rounded-full object-cover border border-slate-100 dark:border-slate-700"
                        />
                      ) : (
                        <div className="w-full h-full rounded-full bg-[#F4F2FF] dark:bg-slate-800 border border-slate-100 dark:border-slate-700 flex items-center justify-center font-bangla font-black text-4xl text-[#00C9A7]">
                          {name.charAt(0)}
                        </div>
                      )}
                    </div>
                    <div className="space-y-2 pt-2 md:pb-2">
                      <h2 className="font-bangla text-2xl md:text-3xl font-black text-slate-800 dark:text-white leading-tight">
                        {name}
                      </h2>
                      <div className="flex flex-wrap items-center gap-3 text-xs md:text-sm font-bangla font-bold">
                        <span className="text-[#00C9A7]">{designation}</span>
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-300" />
                        <div className="flex items-center gap-1 text-amber-500">
                          <Star className="w-4 h-4 fill-amber-500 text-amber-500" />
                          <span>{rating}</span>
                          <span className="text-slate-400 font-medium">({reviewCount.includes('রিভিউ') ? reviewCount : `${reviewCount}টি রিভিউ`})</span>
                        </div>
                        {experience && (
                          <>
                            <span className="w-1.5 h-1.5 rounded-full bg-slate-300" />
                            <span className="text-slate-500 dark:text-slate-400">{experience}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Profile Body Columns */}
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 pt-4 border-t border-slate-100 dark:border-slate-800">
                    {/* Left Column - Stats / Bio Info */}
                    <div className="lg:col-span-4 space-y-6">
                      <div className="bg-slate-50 dark:bg-slate-800/50 rounded-2xl p-5 border border-slate-100/80 dark:border-slate-800 space-y-4">
                        <h4 className="font-bangla text-sm font-extrabold text-slate-800 dark:text-white">সংক্ষিপ্ত বিবরণ</h4>
                        
                        <div className="space-y-3.5">
                          <div className="flex items-center gap-3 text-xs font-bangla">
                            <BookOpen className="w-4 h-4 text-indigo-500" />
                            <div className="text-slate-500 dark:text-slate-400">কোর্স সংখ্যা: <span className="font-extrabold text-slate-800 dark:text-white">{otherCourses.length}টি</span></div>
                          </div>
                          <div className="flex items-center gap-3 text-xs font-bangla">
                            <Users className="w-4 h-4 text-emerald-500" />
                            <div className="text-slate-500 dark:text-slate-400">মোট শিক্ষার্থী: <span className="font-extrabold text-slate-800 dark:text-white">{studentsCount}</span></div>
                          </div>
                          <div className="flex items-center gap-3 text-xs font-bangla">
                            <Star className="w-4 h-4 text-amber-500" />
                            <div className="text-slate-500 dark:text-slate-400">গড় রেটিং: <span className="font-extrabold text-slate-800 dark:text-white">{rating} / ৫.০</span></div>
                          </div>
                        </div>

                        {/* Social Links */}
                        <div className="pt-4 border-t border-slate-200/60 dark:border-slate-700/60 flex items-center gap-3">
                          <a href={facebook} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center hover:scale-105 transition-transform">
                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
                            </svg>
                          </a>
                          <a href={youtube} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 flex items-center justify-center hover:scale-105 transition-transform">
                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z"/>
                              <polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"/>
                            </svg>
                          </a>
                          <a href={linkedin} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full bg-sky-50 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400 flex items-center justify-center hover:scale-105 transition-transform">
                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/>
                              <rect x="2" y="9" width="4" height="12"/>
                              <circle cx="4" cy="4" r="2"/>
                            </svg>
                          </a>
                        </div>
                      </div>
                    </div>

                    {/* Right Column - Full Bio / Details */}
                    <div className="lg:col-span-8 space-y-6">
                      <div className="space-y-3">
                        <h3 className="font-bangla text-lg font-extrabold text-slate-800 dark:text-white">পরিচিতি ও বিবরণ</h3>
                        {bio ? (
                          bio.split('\n').map((para: string, pIdx: number) => (
                            <p key={pIdx} className="font-bangla text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                              {para}
                            </p>
                          ))
                        ) : (
                          <>
                            <p className="font-bangla text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                              শিক্ষা জীবনের দীর্ঘ অভিজ্ঞতায় সমৃদ্ধ ও শিক্ষার্থীদের সহজ ও সাবলীল উপায়ে বিষয়বস্তু আয়ত্ত করতে সহযোগিতা করাই উনার মূল লক্ষ্য। বিভিন্ন টেকনিক্যাল শর্টকাট এবং সৃজনশীল পদ্ধতিতে শিক্ষার্থীদের অ্যাকাডেমিক দক্ষতা বাড়াতে তিনি অবদান রাখছেন।
                            </p>
                            <p className="font-bangla text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                              শুদ্ধশিক্ষা প্ল্যাটফর্মে তিনি গণিত, বিজ্ঞান এবং অন্যান্য গুরুত্বপূর্ণ বিষয়ের মাস্টার ক্লাসের ইনস্ট্রাক্টর হিসেবে যুক্ত আছেন। তার লক্ষ্য হলো শিক্ষার্থীদের অন্তর্দৃষ্টি জাগ্রত করা এবং সহজ পদ্ধতিতে জটিল সমস্যার সমাধান করা।
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Bottom Column - Other Courses */}
                  {otherCourses.length > 0 && (
                    <div className="mt-10 pt-8 border-t border-slate-100 dark:border-slate-800 space-y-5">
                      <h3 className="font-bangla text-base font-extrabold text-slate-800 dark:text-white">
                        ইনস্ট্রাক্টরের অন্যান্য курсসমূহ ({otherCourses.length}টি)
                      </h3>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pb-4">
                        {otherCourses.map((course, cIdx) => (
                          <div 
                            key={cIdx}
                            onClick={() => {
                              setSelectedCourseId(course.id);
                              setSelectedInstructor(null);
                            }}
                            className="bg-slate-50 dark:bg-slate-800/40 rounded-2xl p-4 border border-slate-100 dark:border-slate-800 hover:shadow-md transition-shadow cursor-pointer space-y-3"
                          >
                            {/* Course Image or initial placeholder */}
                            <div className={`h-24 bg-gradient-to-r ${course.color} rounded-xl flex items-center justify-center text-white relative overflow-hidden font-bangla font-black text-sm p-4 text-center`}>
                              {course.title}
                            </div>
                            <div className="space-y-1">
                              <h4 className="font-bangla text-xs font-bold text-slate-800 dark:text-white line-clamp-2 leading-snug">
                                {course.title}
                              </h4>
                              <div className="flex items-center justify-between text-[10px] text-slate-400 font-bangla">
                                <span>{course.totalLessons}টি ক্লাস</span>
                                <span className="text-[#00C9A7] font-extrabold">৳{course.price}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                </div>
              </motion.div>
            </div>
          );
        })()}
      </AnimatePresence>

    </motion.div>
  );
}
